import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchData } from '../hooks/useDataRefresh';

const WW_COLOR    = '#1a8a3f';
const COLES_COLOR = '#e03131';
const BOTH_COLOR  = 'var(--info)';

// ── Region → State mapping ────────────────────────────────────────────────────
const REGION_STATE = {
  // NSW
  'Sydney - Parramatta': 'NSW', 'Sydney - Inner South West': 'NSW', 'Sydney - South West': 'NSW',
  'Sydney - Blacktown': 'NSW', 'Sydney - North Sydney and Hornsby': 'NSW', 'Sydney - Northern Beaches': 'NSW',
  'Sydney - City and Inner South': 'NSW', 'Sydney - Baulkham Hills and Hawkesbury': 'NSW',
  'Sydney - Sutherland': 'NSW', 'Sydney - Inner West': 'NSW', 'Sydney - Ryde': 'NSW',
  'Sydney - Outer South West': 'NSW', 'Sydney - Outer West and Blue Mountains': 'NSW',
  'Illawarra': 'NSW', 'Central Coast': 'NSW', 'Hunter Valley exc Newcastle': 'NSW',
  'Newcastle and Lake Macquarie': 'NSW', 'Richmond - Tweed': 'NSW', 'Mid North Coast': 'NSW',
  'Central West': 'NSW', 'New England and North West': 'NSW', 'Riverina': 'NSW',
  'Capital Region': 'NSW', 'Southern Highlands and Shoalhaven': 'NSW', 'Coffs Harbour - Grafton': 'NSW',
  // VIC
  'Melbourne - South East': 'VIC', 'Melbourne - West': 'VIC', 'Melbourne - North East': 'VIC',
  'Melbourne - Inner': 'VIC', 'Melbourne - Outer East': 'VIC', 'Melbourne - Inner South': 'VIC',
  'Melbourne - Inner East': 'VIC', 'Melbourne - North West': 'VIC',
  'Geelong': 'VIC', 'Mornington Peninsula': 'VIC', 'Latrobe - Gippsland': 'VIC',
  // QLD
  'Gold Coast': 'QLD', 'Logan - Beaudesert': 'QLD', 'Sunshine Coast': 'QLD', 'Ipswich': 'QLD',
  'Moreton Bay - North': 'QLD', 'Moreton Bay - South': 'QLD', 'Brisbane - South': 'QLD',
  'Brisbane - North': 'QLD', 'Brisbane Inner City': 'QLD', 'Brisbane - East': 'QLD',
  'Brisbane - West': 'QLD', 'Central Queensland': 'QLD', 'Wide Bay': 'QLD',
  'Cairns': 'QLD', 'Townsville': 'QLD', 'Mackay - Isaac - Whitsunday': 'QLD', 'Toowoomba': 'QLD',
  // WA
  'Perth - North West': 'WA', 'Perth - South West': 'WA', 'Perth - South East': 'WA',
  'Perth - North East': 'WA', 'Perth - Inner': 'WA', 'Bunbury': 'WA',
  // SA
  'Adelaide - North': 'SA', 'Adelaide - South': 'SA', 'Adelaide - Central and Hills': 'SA',
  'Adelaide - West': 'SA', 'South Australia - South East': 'SA',
  // TAS
  'Hobart': 'TAS', 'Launceston and North East': 'TAS',
  // ACT
  'Australian Capital Territory': 'ACT',
  // NT
  'Darwin': 'NT', 'West and North West': 'NT',
};

const TOURISM_STATE = {
  'Sydney': 'NSW', 'Hunter': 'NSW', 'North Coast NSW': 'NSW', 'South Coast': 'NSW',
  'Central Coast': 'NSW', 'Central NSW': 'NSW', 'New England North West': 'NSW',
  'Riverina': 'NSW', 'Capital Country': 'ACT', 'Snowy Mountains': 'NSW',
  'Blue Mountains': 'NSW',
  'Melbourne': 'VIC', 'Yarra Valley and the Dandenong Ranges': 'VIC', 'Peninsula': 'VIC',
  'Geelong and the Bellarine': 'VIC', 'Great Ocean Road': 'VIC', 'Ballarat': 'VIC',
  'Bendigo Loddon': 'VIC', 'Goulburn': 'VIC', 'Gippsland': 'VIC', 'High Country': 'VIC',
  'Mallee': 'VIC', 'Macedon': 'VIC', 'Lakes': 'VIC', 'Western Grampians': 'VIC',
  'Phillip Island': 'VIC', 'The Murray': 'VIC', 'Central Murray': 'VIC', 'Murray East': 'VIC',
  'Brisbane': 'QLD', 'Gold Coast': 'QLD', 'Sunshine Coast': 'QLD',
  'Southern Queensland Country': 'QLD', 'Tropical North Queensland': 'QLD',
  'Townsville': 'QLD', 'Capricorn': 'QLD', 'Mackay': 'QLD', 'Fraser Coast': 'QLD',
  'Bundaberg': 'QLD', 'Gladstone': 'QLD', 'Outback Queensland': 'QLD', 'Whitsundays': 'QLD',
  'Destination Perth': 'WA', "Australia's South West": 'WA', "Australia's Golden Outback": 'WA',
  "Australia's Coral Coast": 'WA', "Australia's North West": 'WA',
  'Adelaide': 'SA', 'Fleurieu Peninsula': 'SA', 'Flinders Ranges and Outback': 'SA',
  'Eyre Peninsula': 'SA', 'Adelaide Hills': 'SA', 'Riverland': 'SA', 'Limestone Coast': 'SA',
  'Hobart and the South': 'TAS', 'Launceston and the North': 'TAS',
  'Canberra': 'ACT',
  'Darwin': 'NT', 'North West': 'NT', 'Alice Springs': 'NT',
};

// ── Formatters ────────────────────────────────────────────────────────────────
function fmtRSV(v) {
  if (v == null || v === 0) return '—';
  const abs = Math.abs(v), sign = v < 0 ? '-$' : '$';
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${Math.round(abs)}`;
}
function fmtUnits(v) {
  if (v == null || v === 0) return '—';
  const abs = Math.abs(v), sign = v < 0 ? '-' : '';
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}${Math.round(abs).toLocaleString()}`;
}
function fmtDeltaRSV(v) {
  if (v == null) return null;
  const abs = Math.abs(v), sign = v < 0 ? '-$' : '+$';
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${Math.round(abs)}`;
}
function fmtDeltaUnits(v) {
  if (v == null) return null;
  const abs = Math.abs(v), sign = v < 0 ? '-' : '+';
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}${Math.round(abs).toLocaleString()}`;
}

function fmtWeekLabel(w) {
  if (!w) return '';
  const d = new Date(w + 'T00:00:00');
  d.setDate(d.getDate() + 2); // Mon → Wed
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' });
}

// ── Aggregate multiple weeks ──────────────────────────────────────────────────
function sumWeeks(weeksData, key) {
  const agg = {};
  for (const wd of weeksData) {
    for (const row of (wd[key] || [])) {
      if (!agg[row.name]) agg[row.name] = { name: row.name, ww_ty: 0, ww_py: null, coles_ty: 0, coles_py: null, ww_units_ty: 0, ww_units_py: null, coles_units_ty: 0, coles_units_py: null };
      const a = agg[row.name];
      a.ww_ty    += row.ww_ty    || 0;
      a.coles_ty += row.coles_ty || 0;
      if (row.ww_py    != null) a.ww_py    = (a.ww_py    || 0) + row.ww_py;
      if (row.coles_py != null) a.coles_py = (a.coles_py || 0) + row.coles_py;
      a.ww_units_ty    += row.ww_units_ty    || 0;
      a.coles_units_ty += row.coles_units_ty || 0;
      if (row.ww_units_py    != null) a.ww_units_py    = (a.ww_units_py    || 0) + row.ww_units_py;
      if (row.coles_units_py != null) a.coles_units_py = (a.coles_units_py || 0) + row.coles_units_py;
    }
  }
  return Object.values(agg).sort((a, b) => ((b.ww_ty || 0) + (b.coles_ty || 0)) - ((a.ww_ty || 0) + (a.coles_ty || 0)));
}

// state can be a string or string[] for multi-select
function sumWeeksByState(weeksData, key, state) {
  const states = Array.isArray(state) ? state : [state];
  const agg = {};
  for (const wd of weeksData) {
    for (const st of states) {
      for (const row of ((wd[key] || {})[st] || [])) {
        if (!agg[row.name]) agg[row.name] = { name: row.name, ww_ty: 0, ww_py: null, coles_ty: 0, coles_py: null };
        const a = agg[row.name];
        a.ww_ty    += row.ww_ty    || 0;
        a.coles_ty += row.coles_ty || 0;
        if (row.ww_py    != null) a.ww_py    = (a.ww_py    || 0) + row.ww_py;
        if (row.coles_py != null) a.coles_py = (a.coles_py || 0) + row.coles_py;
      }
    }
  }
  return Object.values(agg).sort((a, b) => ((b.ww_ty || 0) + (b.coles_ty || 0)) - ((a.ww_ty || 0) + (a.coles_ty || 0)));
}

// Returns true if value matches a null/string/string[] filter
function matchFilter(filter, value) {
  if (!filter || (Array.isArray(filter) && filter.length === 0)) return true;
  return Array.isArray(filter) ? filter.includes(value) : filter === value;
}

// ── Convert raw rows to display {ty, py} based on retailer filter + measure ──
function toDisplay(rows, retailerFilter, measure = 'RSV') {
  const isRSV = measure === 'RSV';
  const wwTY    = isRSV ? 'ww_ty'         : 'ww_units_ty';
  const wwPY    = isRSV ? 'ww_py'         : 'ww_units_py';
  const colTY   = isRSV ? 'coles_ty'      : 'coles_units_ty';
  const colPY   = isRSV ? 'coles_py'      : 'coles_units_py';

  return (rows || []).map(s => {
    if (retailerFilter === 'ww')    return { name: s.name, ty: s[wwTY]  || 0, py: s[wwPY]  ?? null };
    if (retailerFilter === 'coles') return { name: s.name, ty: s[colTY] || 0, py: s[colPY] ?? null };
    const ty = (s[wwTY] || 0) + (s[colTY] || 0);
    const py = s[wwPY] != null || s[colPY] != null ? (s[wwPY] || 0) + (s[colPY] || 0) : null;
    return { name: s.name, ty, py };
  }).filter(r => r.ty > 0);
}

// ── Bar row ───────────────────────────────────────────────────────────────────
function HRow({ name, value, delta, maxValue, color, nameWidth = 120, dimmed, onClick, measure = 'RSV' }) {
  const pct = maxValue > 0 && value > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  const fv  = measure === 'RSV' ? fmtRSV  : fmtUnits;
  const fd  = measure === 'RSV' ? fmtDeltaRSV : fmtDeltaUnits;
  const ds  = fd(delta);
  return (
    <div onClick={onClick ? (e) => onClick(e) : undefined} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '2px 0', opacity: dimmed ? 0.3 : 1, cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ width: nameWidth, fontSize: 11, color: 'var(--text-primary)', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>{name}</div>
      <div style={{ flex: 1, height: 13, background: 'var(--border)', borderRadius: 2, minWidth: 20 }}>
        {value > 0 && <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2, minWidth: 2 }} />}
      </div>
      <div style={{ width: 64, fontSize: 11, color: 'var(--text-primary)', textAlign: 'right', flexShrink: 0 }}>{value > 0 ? fv(value) : '—'}</div>
      <div style={{ width: 60, fontSize: 11, fontWeight: 600, textAlign: 'right', flexShrink: 0, color: ds == null ? 'var(--text-muted)' : delta >= 0 ? 'var(--success)' : 'var(--danger)' }}>{ds ?? '—'}</div>
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
function HCard({ title, rows, color, nameWidth = 120, maxRows = 50, noDataMsg = 'No data', activeFilter, onRowClick, note, measure = 'RSV' }) {
  if (!rows || !rows.length) {
    return (
      <div className="card" style={{ padding: '10px 14px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{title}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 11, textAlign: 'center', padding: '8px 0' }}>{noDataMsg}</div>
      </div>
    );
  }
  const sorted  = [...rows].sort((a, b) => b.ty - a.ty).slice(0, maxRows);
  const maxVal  = Math.max(...sorted.map(r => r.ty), 1);
  const totalTY = rows.reduce((s, r) => s + (r.ty || 0), 0);
  const hasPY   = rows.some(r => r.py != null);
  const totalPY = hasPY ? rows.reduce((s, r) => s + (r.py || 0), 0) : null;
  const fv      = measure === 'RSV' ? fmtRSV       : fmtUnits;
  const fd      = measure === 'RSV' ? fmtDeltaRSV  : fmtDeltaUnits;

  return (
    <div className="card" style={{ padding: '10px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
        {title}{note && <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, fontSize: 9 }}>{note}</span>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 5, marginBottom: 2 }}>
        <div style={{ width: 64, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>AC ↓</div>
        <div style={{ width: 60, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>ΔPY</div>
      </div>
      {sorted.map(r => (
        <HRow key={r.name} name={r.name} value={r.ty} delta={r.py != null ? r.ty - r.py : null}
          maxValue={maxVal} color={color} nameWidth={nameWidth} measure={measure}
          dimmed={activeFilter != null && (Array.isArray(activeFilter) ? activeFilter.length > 0 : true) && !matchFilter(activeFilter, r.name)}
          onClick={onRowClick ? (e) => onRowClick(r.name, e) : undefined}
        />
      ))}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{ width: nameWidth, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>Total</div>
        <div style={{ flex: 1 }} />
        <div style={{ width: 64, fontSize: 11, color: 'var(--text-primary)', textAlign: 'right', fontWeight: 600 }}>{fv(totalTY)}</div>
        <div style={{ width: 60, fontSize: 11, fontWeight: 700, textAlign: 'right', color: totalPY == null ? 'var(--text-muted)' : (totalTY - totalPY) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
          {totalPY != null ? fd(totalTY - totalPY) : '—'}
        </div>
      </div>
    </div>
  );
}

// ── Store list ────────────────────────────────────────────────────────────────
function StorePanel({ allStores, storeAttrs, retailerFilter, stateFilter, clusterFilter, sizeFilter,
                      measure, weeksData, isDateFiltered, storeWeeklyRsv, regionFilter, tourismFilter,
                      gondolaFrontFilter, gondolaBackFilter, ethnicFilter, resortFilter,
                      nutBayFilter, mpackBayFilter, groupzoneFilter,
                      brandFilter, subcategoryFilter, brandByStore, subcatByStore,
                      assortmentFilter, assortmentByStore, skuFilter, skuByStore,
                      storeFilter, onStoreClick }) {
  const storeKey = (s) => (s.name || '') + '|' + (s.customer || '') + '|' + (s.number || '');
  const selectedStoreKeys = storeFilter ? new Set([].concat(storeFilter)) : null;

  const hasAttrFilter = regionFilter || tourismFilter || gondolaFrontFilter || gondolaBackFilter
    || ethnicFilter || resortFilter || nutBayFilter || mpackBayFilter || groupzoneFilter;
  const hasDimFilter = stateFilter || clusterFilter || sizeFilter || hasAttrFilter
    || (brandFilter && brandByStore && Object.keys(brandByStore).length > 0)
    || (subcategoryFilter && subcatByStore && Object.keys(subcatByStore).length > 0)
    || (assortmentFilter && assortmentByStore && Object.keys(assortmentByStore).length > 0)
    || (skuFilter && skuByStore && Object.keys(skuByStore).length > 0);


  // Always use all_stores for the complete store list.
  // When a date subset is selected AND store_rsv_by_week is available, overlay period-specific RSV.
  // Falls back to per-week top_stores aggregation only when all_stores is empty.
  const source = (() => {
    let raw;
    if (allStores.length > 0) {
      raw = allStores.map(s => {
        const a = storeAttrs[s.name + '|' + (s.number || '')] || {};
        const enriched = { ...s, ...a, fy_rsv_ty: s.rsv_ty };  // preserve FY total for scaling
        // Overlay period RSV when date-filtered and lookup is available
        if (isDateFiltered && storeWeeklyRsv) {
          const key = s.name + '|' + (s.number || '') + '|' + (s.customer || '');
          enriched.rsv_ty = storeWeeklyRsv[key] ?? 0;
          enriched.rsv_py = null; // PY not available in weekly store lookup
        }
        return enriched;
      });
    } else {
      // Fallback: per-week aggregated top stores (used only when all_stores absent)
      if (!weeksData || weeksData.length === 0) return [];
      const agg = {};
      for (const wd of weeksData) {
        for (const s of (wd.top_stores || [])) {
          const key = s.name + '|' + (s.customer || '');
          if (!agg[key]) agg[key] = { ...s, rsv_ty: 0, rsv_py: 0, units_ty: 0 };
          agg[key].rsv_ty   += s.rsv_ty   || 0;
          agg[key].rsv_py   += s.rsv_py   || 0;
          agg[key].units_ty += s.units_ty || 0;
        }
      }
      raw = Object.values(agg).sort((a, b) => b.rsv_ty - a.rsv_ty);
    }

    // Step 1: Merge stores that share name+customer+state (Fabric data duplicates).
    // These are the same physical store appearing twice — sum their RSV values.
    const mergeMap = {};
    for (const s of raw) {
      const mk = s.name + '|' + (s.customer || '') + '|' + (s.state || '');
      if (!mergeMap[mk]) {
        mergeMap[mk] = { ...s };
      } else {
        mergeMap[mk].rsv_ty  = (mergeMap[mk].rsv_ty  || 0) + (s.rsv_ty  || 0);
        mergeMap[mk].rsv_py  = mergeMap[mk].rsv_py != null || s.rsv_py != null
          ? (mergeMap[mk].rsv_py || 0) + (s.rsv_py || 0) : null;
        mergeMap[mk].units_ty = (mergeMap[mk].units_ty || 0) + (s.units_ty || 0);
      }
    }
    const deduped = Object.values(mergeMap);

    // Step 2: Disambiguate stores sharing name+customer across different states.
    // Assign display names so cross-state stores are distinguishable.
    const nameCount = {};
    for (const s of deduped) {
      const k = s.name + '|' + (s.customer || '');
      nameCount[k] = (nameCount[k] || 0) + 1;
    }
    return deduped.map(s => {
      const k = s.name + '|' + (s.customer || '');
      if (nameCount[k] === 1) return s; // unique name — no suffix needed
      // Prefer store number as the clearest identifier; fall back to state
      const suffix = s.number ? ' (#' + s.number + ')' : (s.state ? ' (' + s.state + ')' : '');
      return { ...s, displayName: s.name + suffix };
    });
  })();

  const filtered = source.filter(s => {
    if (retailerFilter === 'ww'    && s.customer !== 'ww')    return false;
    if (retailerFilter === 'coles' && s.customer !== 'coles') return false;
    if (!matchFilter(stateFilter,   s.state))   return false;
    if (!matchFilter(clusterFilter, s.cluster)) return false;
    if (!matchFilter(sizeFilter,    s.size))    return false;
    if (!matchFilter(regionFilter,  s.region))  return false;
    if (!matchFilter(tourismFilter, s.tourism)) return false;
    if (!matchFilter(gondolaFrontFilter, s.gondola_front)) return false;
    if (!matchFilter(gondolaBackFilter,  s.gondola_back))  return false;
    if (!matchFilter(ethnicFilter,   s.ethnic))    return false;
    if (!matchFilter(groupzoneFilter, s.groupzone)) return false;
    if (resortFilter) {
      const resortArr = Array.isArray(resortFilter) ? resortFilter : [resortFilter];
      const resortExpected = resortArr.map(v => v === 'Resort' ? 'Y' : 'N');
      if (!resortExpected.includes(s.resort)) return false;
    }
    if (nutBayFilter) {
      const vals = Array.isArray(nutBayFilter) ? nutBayFilter : [nutBayFilter];
      if (!vals.some(v => parseFloat(s.nut_bay) === parseFloat(v))) return false;
    }
    if (mpackBayFilter) {
      const vals = Array.isArray(mpackBayFilter) ? mpackBayFilter : [mpackBayFilter];
      if (!vals.some(v => parseFloat(s.multipacks_bay) === parseFloat(v))) return false;
    }
    if (brandFilter && brandByStore) {
      const names = Array.isArray(brandFilter) ? brandFilter : [brandFilter];
      const sb = brandByStore[s.name + '|' + (s.number || '')] || {};
      if (!names.some(b => sb[b] != null)) return false;
    }
    if (subcategoryFilter && subcatByStore) {
      const names = Array.isArray(subcategoryFilter) ? subcategoryFilter : [subcategoryFilter];
      const ss = subcatByStore[s.name + '|' + (s.number || '')] || {};
      if (!names.some(b => ss[b] != null)) return false;
    }
    if (assortmentFilter && assortmentByStore) {
      const names = Array.isArray(assortmentFilter) ? assortmentFilter : [assortmentFilter];
      const ss = assortmentByStore[s.name + '|' + (s.number || '')] || {};
      if (!names.some(b => ss[b] != null)) return false;
    }
    if (skuFilter && skuByStore) {
      const names = Array.isArray(skuFilter) ? skuFilter : [skuFilter];
      const ss = skuByStore[s.name + '|' + (s.number || '')] || {};
      if (!names.some(b => ss[b] != null)) return false;
    }
    return true;
  });

  const valKey  = measure === 'Units' ? 'units_ty' : 'rsv_ty';
  const pyKey   = measure === 'Units' ? 'units_py' : 'rsv_py';

  // When SKU or assortment filter active: rank/display stores by that product's sales
  const storeSkuVals = ((skuFilter && skuByStore) || (assortmentFilter && assortmentByStore)) ? (() => {
    const useSku = skuFilter && skuByStore;
    const lookup = useSku ? skuByStore : assortmentByStore;
    const selectedNames = useSku
      ? (Array.isArray(skuFilter) ? skuFilter : [skuFilter])
      : (Array.isArray(assortmentFilter) ? assortmentFilter : [assortmentFilter]);
    const result = {};
    for (const s of filtered) {
      const storeLookup = lookup[s.name + '|' + (s.number || '')] || {};
      let ty = 0, py = null, uty = 0;
      for (const name of selectedNames) {
        const sv = storeLookup[name];
        if (!sv) continue;
        ty  += (s.customer === 'ww' ? sv.ww_ty  : sv.coles_ty)  || 0;
        uty += (s.customer === 'ww' ? sv.ww_units_ty : sv.coles_units_ty) || 0;
        const svpy = s.customer === 'ww' ? sv.ww_py : sv.coles_py;
        if (svpy != null) py = (py || 0) + svpy;
      }
      // Scale FY product RSV to weekly estimate when date-filtered
      if (isDateFiltered && storeWeeklyRsv && (s.fy_rsv_ty || 0) > 0) {
        const scale = (s.rsv_ty || 0) / s.fy_rsv_ty;
        ty = ty * scale;
        if (py != null) py = py * scale;
        uty = uty * scale;
      }
      // Use displayName as key so same-name cross-state stores don't collide
      result[(s.displayName || s.name) + '|' + s.customer] = { ty, py, uty };
    }
    return result;
  })() : null;

  if (!filtered.length) {
    return (
      <div className="card" style={{ padding: '10px 14px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Store Measure vs. PY</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 11, textAlign: 'center', padding: '20px 0' }}>No stores match current filters</div>
      </div>
    );
  }
  const getStoreVal = s => storeSkuVals
    ? (storeSkuVals[(s.displayName || s.name) + '|' + s.customer]?.[measure === 'Units' ? 'uty' : 'ty'] || 0)
    : (s[valKey] || s.rsv_ty || 0);
  const getStorePy = s => storeSkuVals
    ? (storeSkuVals[(s.displayName || s.name) + '|' + s.customer]?.py ?? null)
    : (s[pyKey] ?? null);
  const sorted   = [...filtered].sort((a, b) => getStoreVal(b) - getStoreVal(a));
  const display  = sorted.slice(0, 200);
  const maxVal   = Math.max(...display.map(s => getStoreVal(s)), 1);
  const totalTY  = filtered.reduce((sum, s) => sum + getStoreVal(s), 0);
  const fv       = measure === 'RSV' ? fmtRSV : fmtUnits;
  const storeTitle = filtered.length > 200
    ? `Store Measure vs. PY — Top 200 of ${filtered.length}`
    : `Store Measure vs. PY — ${filtered.length} stores`;

  return (
    <div className="card" style={{ padding: '10px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{storeTitle}</div>
      {isDateFiltered && !storeWeeklyRsv && (
        <div style={{ fontSize: 9, color: 'var(--warn-text)', background: 'var(--warn-soft)', border: '1px solid var(--warn-border)', borderRadius: 4, padding: '3px 7px', marginBottom: 6 }}>
          Weekly store data unavailable for selected period — showing full-year totals. Re-run build_store_weekly_panels.py to update.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 5, marginBottom: 2 }}>
        <div style={{ width: 64, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>AC ↓</div>
        <div style={{ width: 60, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>ΔPY</div>
      </div>
      {display.map((s, i) => {
        const v = getStoreVal(s);
        const p = getStorePy(s);
        const k = storeKey(s);
        const selected = selectedStoreKeys?.has(k);
        const dimmed = selectedStoreKeys && !selected;
        return (
          <HRow key={i} name={s.displayName || s.name} value={v} delta={p != null ? v - p : null}
            maxValue={maxVal} color={s.customer === 'coles' ? COLES_COLOR : WW_COLOR}
            nameWidth={188} measure={measure}
            dimmed={dimmed}
            onClick={onStoreClick ? (e) => onStoreClick(k, e) : undefined}
          />
        );
      })}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{ width: 188, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>Total</div>
        <div style={{ flex: 1 }} />
        <div style={{ width: 64, fontSize: 11, color: 'var(--text-primary)', textAlign: 'right', fontWeight: 600 }}>{fv(totalTY)}</div>
        <div style={{ width: 60 }} />
      </div>
    </div>
  );
}

// Assortment classification — matches SCANTOTALGROCERY promotable range groupings
const ASSORTMENT_MAP = {
  // ── BHUJA ─────────────────────────────────────────────────────────────────
  // BHUJA 70-200G (core everyday range)
  'BHUJA ORIGINAL MIX 150G':          'BHUJA 70-200G',
  'BHUJA ORIGINAL MIX 180G':          'BHUJA 70-200G',
  'BHUJA BEER MIX 160G':              'BHUJA 70-200G',
  'BHUJA NUT MIX 140G':               'BHUJA 70-200G',
  'BHUJA CRUNCHY PEAS 140G':          'BHUJA 70-200G',
  'BHUJA CRACKER MIX 150G':           'BHUJA 70-200G',
  'BHUJA CRACKER MIX 180G':           'BHUJA 70-200G',
  'BHUJA ANCIENT GRAIN TWISTS 140G':  'BHUJA 70-200G',
  'BHUJA SNACK SAFARI AUSSIE 70G':    'BHUJA 70-200G',
  'BHUJA SNACK SAFARI MEXICAN 70G':   'BHUJA 70-200G',
  'BHUJA ZINGY MIX 150G':             'BHUJA 70-200G',
  'BHUJA ALOO BHUJIA DESI 140G':      'BHUJA 70-200G',
  // BHUJA 300-400G
  'BHUJA ORIGINAL MIX 350G':          'BHUJA 300-400G',
  // BHUJA 1KG
  'BHUJA ORIGINAL MIX 1KG':           'BHUJA 1KG',
  // BHUJA AUTHENTIC INDIAN 70-150G
  'BHUJA ALOO BHUJIA 140G':           'BHUJA AUTHENTIC INDIAN 70-150G',
  'BHUJA MURUKKU 150G':               'BHUJA AUTHENTIC INDIAN 70-150G',
  'BHUJA PAPDI GATHIYA 70G':          'BHUJA AUTHENTIC INDIAN 70-150G',
  'BHUJA ALOO DESI SNACKS 140G':      'BHUJA AUTHENTIC INDIAN 70-150G',
  // BHUJA 40-50G (convenience / snack packs)
  'BHUJA ORIGINAL MIX 50G':           'BHUJA 40-50G',
  'BHUJA BEER MIX 50G':               'BHUJA 40-50G',
  // ── INFUZIONS ─────────────────────────────────────────────────────────────
  // INFUZIONS MULTIPACK 50-75G
  'INFUZIONS VEGGIE STRAWS SOUR CREAM & HERB MULTIPACK 5 PACK 75G': 'INFUZIONS MULTIPACK 50-75G',
  'INFUZIONS VEGGIE RINGS AUSSIE BBQ MULTIPACK 5 PACK 75G':         'INFUZIONS MULTIPACK 50-75G',
  'INFUZIONS VEGGIE STRAWS SALT & VINEGAR MULTIPACK 5 PACK 75G':    'INFUZIONS MULTIPACK 50-75G',
  'INFUZIONS POPPEAS BLUEY BACKYARD BBQ 5 PACK 50G':                'INFUZIONS MULTIPACK 50-75G',
  'INFUZIONS POPPEAS BLUEY CHEESY PEASY 5 PACK 50G':                'INFUZIONS MULTIPACK 50-75G',
  // INFUZIONS 90-110G (standard single-serve)
  'INFUZIONS VEGGIE STRAWS SOUR CREAM & HERB 90G':   'INFUZIONS 90-110G',
  'INFUZIONS VEGGIE STRAWS SOUR CREAM & HERB 100G':  'INFUZIONS 90-110G',
  'INFUZIONS VEGGIE STRAWS SOUR CREAM & HERBS 110G': 'INFUZIONS 90-110G',
  'INFUZIONS VEGGIE RINGS AUS BBQ 90G':               'INFUZIONS 90-110G',
  'INFUZIONS VEGGIE STRAWS SALT & VINEGAR 90G':       'INFUZIONS 90-110G',
  'INFUZIONS PRAWN CRACKER BBQ 90G':                  'INFUZIONS 90-110G',
  'INFUZIONS BBQ RIB PRAWN CRACKERS 110G':            'INFUZIONS 90-110G',
  'INFUZIONS POTATO MIX THAI SWEET CHILLI 100G':      'INFUZIONS 90-110G',
  // INFUZIONS AIR FRIES SHARE PACK 90G
  'INFUZIONS AIR FRIES AIOLI 90G':       'INFUZIONS AIR FRIES SHARE PACK 90G',
  'INFUZIONS AIR FRIES PERI PERI 90G':   'INFUZIONS AIR FRIES SHARE PACK 90G',
  'INFUZIONS AIR FRIES TOMATO SAUCE 90G':'INFUZIONS AIR FRIES SHARE PACK 90G',
  // INFUZIONS AIR FRIES 5 PACK 75G
  'INFUZIONS AIR FRIES AIOLI 75G':       'INFUZIONS AIR FRIES 5 PACK 75G',
  // ── SMART SNACK ───────────────────────────────────────────────────────────
  'SMART SNACK LENTIL CHIPS CHEDDAR & SOUR CREAM 100G': 'SMART SNACK 100G',
};

// Normalize brand names: fix mixed-case and "Infuzions Multipack" → "INFUZIONS"
const BRAND_NORM = {
  'bhuja':              'BHUJA',
  'infuzions':          'INFUZIONS',
  'infuzions multipack':'INFUZIONS',
  'smart snack':        'SMART SNACK',
};
function normalizeBrand(name) {
  return BRAND_NORM[(name || '').toLowerCase().trim()] || (name || '').toUpperCase().trim() || name;
}

function deriveAssortment(skuName) {
  return ASSORTMENT_MAP[(skuName || '').trim()] || skuName || '';
}

// ── Shared helpers for hierarchy cards ───────────────────────────────────────
function _hierIsActive(filter, val) {
  if (!filter || (Array.isArray(filter) && filter.length === 0)) return false;
  return Array.isArray(filter) ? filter.includes(val) : filter === val;
}
function _hierIsDimmed(filter, val) {
  if (!filter || (Array.isArray(filter) && filter.length === 0)) return false;
  return !_hierIsActive(filter, val);
}

// Shared BarRow for hierarchy cards — receives hasPY from parent scope via prop
function HierBarRow({ name, ty, py, hasPY, maxV, indent, fontSize, fontWeight, color, dimmed, selected,
                      onClick, onToggle, hasChildren, childrenCollapsed, fv, fd,
                      NAME_W = 110, PY_W = 58, AC_W = 64, DPY_W = 62 }) {
  const pct   = maxV > 0 && ty > 0 ? Math.min((ty / maxV) * 100, 100) : 0;
  const delta = py != null ? ty - py : null;
  const ds    = fd(delta);
  return (
    <div style={{ opacity: dimmed ? 0.3 : 1, background: selected ? 'rgba(59,130,246,0.08)' : 'transparent', borderRadius: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 0', paddingLeft: indent, cursor: 'pointer' }} onClick={onClick}>
        <div style={{ width: 10, fontSize: 10, color: hasChildren ? 'var(--info)' : 'var(--text-muted)', flexShrink: 0, textAlign: 'center', cursor: hasChildren ? 'pointer' : 'default' }}
          onClick={e => { if (hasChildren) { e.stopPropagation(); onToggle?.(); } }}>
          {hasChildren ? (childrenCollapsed ? '▸' : '▾') : ''}
        </div>
        <div style={{ width: NAME_W, fontSize, fontWeight, color: selected ? 'var(--info)' : (color || 'var(--text-primary)'), flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>
          {name}
        </div>
        {hasPY && <div style={{ width: PY_W, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>{py != null ? fv(py) : '—'}</div>}
        <div style={{ flex: 1, height: 11, background: 'var(--border)', borderRadius: 2, minWidth: 20 }}>
          {ty > 0 && <div style={{ width: `${pct}%`, height: '100%', background: BOTH_COLOR, borderRadius: 2, opacity: fontWeight === 700 ? 1 : 0.7, minWidth: 2 }} />}
        </div>
        <div style={{ width: AC_W, fontSize, color: selected ? 'var(--info)' : 'var(--text-primary)', textAlign: 'right', flexShrink: 0 }}>{ty > 0 ? fv(ty) : '—'}</div>
        <div style={{ width: DPY_W, fontSize, fontWeight: 600, textAlign: 'right', flexShrink: 0, color: ds == null ? 'var(--text-muted)' : delta >= 0 ? 'var(--success)' : 'var(--danger)' }}>{ds ?? '—'}</div>
      </div>
    </div>
  );
}

// ── Expandable Retailer → Cluster card (2-level hierarchy) ───────────────────
function ExpandableClusterCard({ rawRows, rf, measure, activeFilter, onItemClick, note }) {
  const [expandedRetailers, setExpandedRetailers] = useState({});

  const fv = measure === 'RSV' ? fmtRSV : fmtUnits;
  const fd = measure === 'RSV' ? fmtDeltaRSV : fmtDeltaUnits;

  // Derive retailer from which customer column has values
  function retailerOf(row) {
    if ((row.ww_ty || 0) > 0 && !(row.coles_ty > 0)) return 'ww';
    if ((row.coles_ty || 0) > 0 && !(row.ww_ty > 0)) return 'coles';
    return (row.ww_ty || 0) >= (row.coles_ty || 0) ? 'ww' : 'coles';
  }
  function getVal(row) {
    return rf === 'ww' ? (row.ww_ty || 0) : rf === 'coles' ? (row.coles_ty || 0) : (row.ww_ty || 0) + (row.coles_ty || 0);
  }
  function getPY(row) {
    const wpy = row.ww_py, cpy = row.coles_py;
    if (rf === 'ww') return wpy;
    if (rf === 'coles') return cpy;
    return (wpy != null || cpy != null) ? (wpy || 0) + (cpy || 0) : null;
  }

  // Group by retailer, skip clusters that belong to the filtered-out retailer
  const SKIP = ['N/A', 'TBC', 'UNKNOWN'];
  const byRetailer = { ww: [], coles: [] };
  for (const row of rawRows) {
    if (!row.name || SKIP.includes(row.name)) continue;
    const ret = retailerOf(row);
    if (rf && rf !== ret) continue;
    byRetailer[ret].push({ name: row.name, ty: getVal(row), py: getPY(row) });
  }

  const allItems = [...byRetailer.ww, ...byRetailer.coles];
  if (!allItems.length) return (
    <div className="card" style={{ padding: '10px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
        Customer Cluster vs. PY{note && <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, fontSize: 9 }}>{note}</span>}
      </div>
      <div style={{ color: 'var(--text-muted)', fontSize: 11, textAlign: 'center', padding: '8px 0' }}>No data</div>
    </div>
  );

  const maxVal      = Math.max(...allItems.map(r => r.ty), 1);
  const visibleItems = activeFilter ? allItems.filter(r => _hierIsActive(activeFilter, r.name)) : allItems;
  const totalTY = visibleItems.reduce((s, r) => s + (r.ty || 0), 0);
  const hasPY   = allItems.some(r => r.py != null);
  const totalPY = hasPY ? visibleItems.reduce((s, r) => s + (r.py || 0), 0) : null;
  const NAME_W = 110; const PY_W = 58; const AC_W = 64; const DPY_W = 62;
  const RETAILER_LABEL = { ww: 'Woolworths', coles: 'Coles' };
  const RETAILER_COLOR = { ww: WW_COLOR, coles: COLES_COLOR };
  const retailerOrder = rf === 'ww' ? ['ww'] : rf === 'coles' ? ['coles'] : ['ww', 'coles'];

  return (
    <div className="card" style={{ padding: '10px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
        Customer Cluster vs. PY{note && <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, fontSize: 9 }}>{note}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
        <div style={{ width: 10, flexShrink: 0 }} /><div style={{ width: NAME_W, flexShrink: 0 }} />
        {hasPY && <div style={{ width: PY_W, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>PY</div>}
        <div style={{ flex: 1 }} />
        <div style={{ width: AC_W, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>AC ↓</div>
        <div style={{ width: DPY_W, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>ΔPY</div>
      </div>
      {retailerOrder.map(ret => {
        const items = byRetailer[ret].slice().sort((a, b) => b.ty - a.ty);
        if (!items.length) return null;
        const retTY = items.reduce((s, r) => s + r.ty, 0);
        const retPY = items.some(r => r.py != null) ? items.reduce((s, r) => s + (r.py || 0), 0) : null;
        const isCollapsed = !expandedRetailers[ret];
        return (
          <div key={ret}>
            <HierBarRow name={RETAILER_LABEL[ret]} ty={retTY} py={retPY} hasPY={hasPY} maxV={maxVal}
              indent={0} fontSize={11} fontWeight={700} color={RETAILER_COLOR[ret]}
              hasChildren={true} childrenCollapsed={isCollapsed}
              onToggle={() => setExpandedRetailers(p => ({ ...p, [ret]: !p[ret] }))}
              onClick={() => {}} fv={fv} fd={fd} NAME_W={NAME_W} PY_W={PY_W} AC_W={AC_W} DPY_W={DPY_W}
            />
            {!isCollapsed && items.map(item => (
              <HierBarRow key={item.name} name={item.name} ty={item.ty} py={item.py} hasPY={hasPY} maxV={maxVal}
                indent={10} fontSize={10} fontWeight={600} color='var(--text-primary)'
                selected={_hierIsActive(activeFilter, item.name)} dimmed={_hierIsDimmed(activeFilter, item.name)}
                onClick={e => onItemClick?.(item.name, e)} fv={fv} fd={fd} NAME_W={NAME_W} PY_W={PY_W} AC_W={AC_W} DPY_W={DPY_W}
              />
            ))}
          </div>
        );
      })}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{ width: 10, flexShrink: 0 }} />
        <div style={{ width: NAME_W, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>Total</div>
        {hasPY && <div style={{ width: PY_W, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>{totalPY != null ? fv(totalPY) : '—'}</div>}
        <div style={{ flex: 1 }} />
        <div style={{ width: AC_W, fontSize: 11, color: 'var(--text-primary)', textAlign: 'right', fontWeight: 600 }}>{fv(totalTY)}</div>
        <div style={{ width: DPY_W, fontSize: 11, fontWeight: 700, textAlign: 'right', color: totalPY == null ? 'var(--text-muted)' : (totalTY - totalPY) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
          {totalPY != null ? fd(totalTY - totalPY) : '—'}
        </div>
      </div>
    </div>
  );
}

// ── Expandable Retailer → Zone Number → Group/Zone card (3-level hierarchy) ──
function ExpandableGroupZoneCard({ rawRows, rf, measure, activeFilter, onGroupZoneClick, note }) {
  const [expandedRetailers, setExpandedRetailers] = useState({});
  const [expandedZones,     setExpandedZones]     = useState({});

  const fv = measure === 'RSV' ? fmtRSV : fmtUnits;
  const fd = measure === 'RSV' ? fmtDeltaRSV : fmtDeltaUnits;

  // Derive retailer: Coles zones start with a letter, WW zones start with a digit
  function retailerOf(gz) { return /^[A-Z]/.test(gz || '') ? 'coles' : 'ww'; }

  // Derive zone number (level 2) — WW: first digit; Coles: letter + first digit
  const COLES_STATE = { N: 'NSW', Q: 'QLD', V: 'VIC', S: 'SA', W: 'WA' };
  function zoneNumberOf(gz) {
    if (!gz) return '';
    if (/^[A-Z]/.test(gz)) {
      const m = gz.match(/^([A-Z]+)(\d)/);
      return m ? m[1] + m[2] : gz.slice(0, 2);
    }
    return gz[0];
  }
  function zoneLabel(zn, ret) {
    if (ret === 'coles') {
      const letter = zn[0], digit = zn.slice(1);
      const state = COLES_STATE[letter] || letter;
      return `${state} Zone ${digit}`;
    }
    return `Zone ${zn}`;
  }

  function getVal(row) {
    return rf === 'ww' ? (row.ww_ty || 0) : rf === 'coles' ? (row.coles_ty || 0) : (row.ww_ty || 0) + (row.coles_ty || 0);
  }
  function getPY(row) {
    const wpy = row.ww_py, cpy = row.coles_py;
    if (rf === 'ww') return wpy;
    if (rf === 'coles') return cpy;
    return (wpy != null || cpy != null) ? (wpy || 0) + (cpy || 0) : null;
  }

  // Build hierarchy: retailer → zoneNum → { ty, py, groups[] }
  const hierarchy = { ww: {}, coles: {} };
  for (const row of rawRows) {
    if (!row.name) continue;
    const ret = retailerOf(row.name);
    if (rf && rf !== ret) continue;
    const zn = zoneNumberOf(row.name);
    const ty = getVal(row), py = getPY(row);
    if (!hierarchy[ret][zn]) hierarchy[ret][zn] = { ty: 0, py: null, groups: [] };
    hierarchy[ret][zn].ty += ty;
    if (py != null) hierarchy[ret][zn].py = (hierarchy[ret][zn].py || 0) + py;
    hierarchy[ret][zn].groups.push({ name: row.name, ty, py });
  }

  const allItems = Object.values(hierarchy).flatMap(z => Object.values(z).flatMap(zd => zd.groups));
  if (!allItems.length) return (
    <div className="card" style={{ padding: '10px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>
        Customer Group Zone vs. PY{note && <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, fontSize: 9 }}>{note}</span>}
      </div>
      <div style={{ color: 'var(--text-muted)', fontSize: 11, textAlign: 'center', padding: '8px 0' }}>No data</div>
    </div>
  );

  const maxVal       = Math.max(...allItems.map(r => r.ty), 1);
  const visibleItems = activeFilter ? allItems.filter(r => _hierIsActive(activeFilter, r.name)) : allItems;
  const totalTY = visibleItems.reduce((s, r) => s + (r.ty || 0), 0);
  const hasPY   = allItems.some(r => r.py != null);
  const totalPY = hasPY ? visibleItems.reduce((s, r) => s + (r.py || 0), 0) : null;
  const NAME_W = 110; const PY_W = 58; const AC_W = 64; const DPY_W = 62;
  const RETAILER_LABEL = { ww: 'Woolworths', coles: 'Coles' };
  const RETAILER_COLOR = { ww: WW_COLOR, coles: COLES_COLOR };
  const retailerOrder = rf === 'ww' ? ['ww'] : rf === 'coles' ? ['coles'] : ['ww', 'coles'];

  return (
    <div className="card" style={{ padding: '10px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
        Customer Group Zone vs. PY{note && <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, fontSize: 9 }}>{note}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
        <div style={{ width: 10, flexShrink: 0 }} /><div style={{ width: NAME_W, flexShrink: 0 }} />
        {hasPY && <div style={{ width: PY_W, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>PY</div>}
        <div style={{ flex: 1 }} />
        <div style={{ width: AC_W, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>AC ↓</div>
        <div style={{ width: DPY_W, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>ΔPY</div>
      </div>
      {retailerOrder.map(ret => {
        const zones = hierarchy[ret];
        if (!Object.keys(zones).length) return null;
        const retTY = Object.values(zones).reduce((s, z) => s + z.ty, 0);
        const retPY = Object.values(zones).some(z => z.py != null)
          ? Object.values(zones).reduce((s, z) => s + (z.py || 0), 0) : null;
        const isRetCollapsed = !expandedRetailers[ret];
        return (
          <div key={ret}>
            {/* Level 1: Retailer */}
            <HierBarRow name={RETAILER_LABEL[ret]} ty={retTY} py={retPY} hasPY={hasPY} maxV={maxVal}
              indent={0} fontSize={11} fontWeight={700} color={RETAILER_COLOR[ret]}
              hasChildren={true} childrenCollapsed={isRetCollapsed}
              onToggle={() => setExpandedRetailers(p => ({ ...p, [ret]: !p[ret] }))}
              onClick={() => {}} fv={fv} fd={fd} NAME_W={NAME_W} PY_W={PY_W} AC_W={AC_W} DPY_W={DPY_W}
            />
            {/* Level 2: Zone Numbers */}
            {!isRetCollapsed && Object.entries(zones).sort(([a], [b]) => a.localeCompare(b)).map(([zn, zd]) => {
              const znKey = ret + '|' + zn;
              const isZnCollapsed = !expandedZones[znKey];
              const znGroups = zd.groups.slice().sort((a, b) => b.ty - a.ty);
              const allCodes = znGroups.map(g => g.name);
              const znAllActive = allCodes.every(c => _hierIsActive(activeFilter, c));
              const znAnyActive = allCodes.some(c => _hierIsActive(activeFilter, c));
              const znDimmed    = !!(activeFilter && (Array.isArray(activeFilter) ? activeFilter.length > 0 : true) && !znAnyActive);
              return (
                <div key={zn}>
                  <HierBarRow name={zoneLabel(zn, ret)} ty={zd.ty} py={zd.py} hasPY={hasPY} maxV={maxVal}
                    indent={10} fontSize={10} fontWeight={600} color='var(--text-primary)'
                    selected={znAllActive} dimmed={znDimmed}
                    hasChildren={znGroups.length > 1} childrenCollapsed={isZnCollapsed}
                    onToggle={() => setExpandedZones(p => ({ ...p, [znKey]: !p[znKey] }))}
                    onClick={e => onGroupZoneClick?.(allCodes, e)}
                    fv={fv} fd={fd} NAME_W={NAME_W} PY_W={PY_W} AC_W={AC_W} DPY_W={DPY_W}
                  />
                  {/* Level 3: Group/Zone Codes */}
                  {(!isZnCollapsed || znGroups.length === 1) && znGroups.map(g => (
                    <HierBarRow key={g.name} name={g.name} ty={g.ty} py={g.py} hasPY={hasPY} maxV={maxVal}
                      indent={20} fontSize={10} fontWeight={500} color='var(--text-body)'
                      selected={_hierIsActive(activeFilter, g.name)} dimmed={_hierIsDimmed(activeFilter, g.name)}
                      onClick={e => onGroupZoneClick?.(g.name, e)}
                      fv={fv} fd={fd} NAME_W={NAME_W} PY_W={PY_W} AC_W={AC_W} DPY_W={DPY_W}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{ width: 10, flexShrink: 0 }} />
        <div style={{ width: NAME_W, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>Total</div>
        {hasPY && <div style={{ width: PY_W, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>{totalPY != null ? fv(totalPY) : '—'}</div>}
        <div style={{ flex: 1 }} />
        <div style={{ width: AC_W, fontSize: 11, color: 'var(--text-primary)', textAlign: 'right', fontWeight: 600 }}>{fv(totalTY)}</div>
        <div style={{ width: DPY_W, fontSize: 11, fontWeight: 700, textAlign: 'right', color: totalPY == null ? 'var(--text-muted)' : (totalTY - totalPY) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
          {totalPY != null ? fd(totalTY - totalPY) : '—'}
        </div>
      </div>
    </div>
  );
}

// ── Expandable Brand → Assortment → SKU card (3-level hierarchy) ─────────────
function ExpandableBrandCard({
  title, brandRows, assortmentsByBrand, activeFilter, onBrandClick,
  assortmentFilter, onAssortmentClick, skuFilter, onSkuClick, note, measure = 'RSV',
  retailerFilter
}) {
  const [expandedBrands,      setExpandedBrands]      = useState({});
  const [expandedAssortments, setExpandedAssortments] = useState({});

  if (!brandRows || !brandRows.length) {
    return (
      <div className="card" style={{ padding: '10px 14px' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{title}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 11, textAlign: 'center', padding: '8px 0' }}>No data</div>
      </div>
    );
  }

  const fv = measure === 'RSV' ? fmtRSV  : fmtUnits;
  const fd = measure === 'RSV' ? fmtDeltaRSV : fmtDeltaUnits;
  const sorted  = [...brandRows].sort((a, b) => b.ty - a.ty);
  const maxVal  = Math.max(...sorted.map(r => r.ty), 1);
  const visibleRows = activeFilter
    ? sorted.filter(r => Array.isArray(activeFilter) ? activeFilter.includes(r.name) : activeFilter === r.name)
    : sorted;
  const totalTY = visibleRows.reduce((s, r) => s + (r.ty || 0), 0);
  const hasPY   = sorted.some(r => r.py != null);
  const totalPY = hasPY ? visibleRows.reduce((s, r) => s + (r.py || 0), 0) : null;

  const NAME_W = 110;
  const PY_W   = 58;
  const AC_W   = 64;
  const DPY_W  = 62;
  const barColor = retailerFilter === 'ww' ? WW_COLOR : retailerFilter === 'coles' ? COLES_COLOR : BOTH_COLOR;

  function isActive(filter, val) {
    if (!filter || (Array.isArray(filter) && filter.length === 0)) return false;
    return Array.isArray(filter) ? filter.includes(val) : filter === val;
  }
  function isDimmedBy(filter, val) {
    if (!filter || (Array.isArray(filter) && filter.length === 0)) return false;
    return !isActive(filter, val);
  }

  // Inline bar row (brand + assortment levels)
  function BarRow({ name, ty, py, maxV, indent, fontSize, fontWeight, color, dimmed, selected, onClick, onToggle, hasChildren, childrenCollapsed }) {
    const pct   = maxV > 0 && ty > 0 ? Math.min((ty / maxV) * 100, 100) : 0;
    const delta = py != null ? ty - py : null;
    const ds    = fd(delta);
    return (
      <div style={{ opacity: dimmed ? 0.3 : 1, background: selected ? 'rgba(59,130,246,0.08)' : 'transparent', borderRadius: 3 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 0', paddingLeft: indent, cursor: 'pointer' }}
          onClick={onClick}>
          <div style={{ width: 10, fontSize: 10, color: hasChildren ? 'var(--info)' : 'var(--text-muted)', flexShrink: 0, textAlign: 'center', cursor: hasChildren ? 'pointer' : 'default' }}
            onClick={e => { if (hasChildren) { e.stopPropagation(); onToggle?.(); } }}>
            {hasChildren ? (childrenCollapsed ? '▸' : '▾') : ''}
          </div>
          <div style={{ width: NAME_W, fontSize, fontWeight, color: selected ? 'var(--info)' : (color || 'var(--text-primary)'), flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>
            {name}
          </div>
          {hasPY && <div style={{ width: PY_W, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>{py != null ? fv(py) : '—'}</div>}
          <div style={{ flex: 1, height: 11, background: 'var(--border)', borderRadius: 2, minWidth: 20 }}>
            {ty > 0 && <div style={{ width: `${pct}%`, height: '100%', background: color || BOTH_COLOR, borderRadius: 2, opacity: fontWeight === 700 ? 1 : 0.7, minWidth: 2 }} />}
          </div>
          <div style={{ width: AC_W, fontSize, color: selected ? 'var(--info)' : 'var(--text-primary)', textAlign: 'right', flexShrink: 0 }}>{ty > 0 ? fv(ty) : '—'}</div>
          <div style={{ width: DPY_W, fontSize, fontWeight: 600, textAlign: 'right', flexShrink: 0, color: ds == null ? 'var(--text-muted)' : delta >= 0 ? 'var(--success)' : 'var(--danger)' }}>{ds ?? '—'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '10px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
        {title}{note && <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, fontSize: 9 }}>{note}</span>}
      </div>
      {/* Column headers */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
        <div style={{ width: 10, flexShrink: 0 }} />
        <div style={{ width: NAME_W, flexShrink: 0 }} />
        {hasPY && <div style={{ width: PY_W, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>PY</div>}
        <div style={{ flex: 1 }} />
        <div style={{ width: AC_W,  fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>AC ↓</div>
        <div style={{ width: DPY_W, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>ΔPY</div>
      </div>

      {sorted.map(brand => {
        const brandCollapsed  = !expandedBrands[brand.name];
        const brandDimmed     = isDimmedBy(activeFilter, brand.name);
        const brandSelected   = isActive(activeFilter, brand.name);
        const assortments     = assortmentsByBrand?.[brand.name] || {};
        const assortmentNames = Object.keys(assortments).sort((a, b) => {
          const tyA = assortments[a].reduce((s, r) => s + (r.ty || 0), 0);
          const tyB = assortments[b].reduce((s, r) => s + (r.ty || 0), 0);
          return tyB - tyA;
        });
        const hasAssortments  = assortmentNames.length > 0;
        const assortMaxVal    = hasAssortments
          ? Math.max(...assortmentNames.map(a => assortments[a].reduce((s, r) => s + (r.ty || 0), 0)), 1)
          : 1;

        return (
          <div key={brand.name} style={{ opacity: brandDimmed ? 0.3 : 1 }}>
            {/* Level 1: Brand */}
            <BarRow
              name={brand.name} ty={brand.ty} py={brand.py} maxV={maxVal}
              indent={0} fontSize={11} fontWeight={700} color={barColor}
              selected={brandSelected}
              hasChildren={hasAssortments} childrenCollapsed={brandCollapsed}
              onToggle={() => setExpandedBrands(p => ({ ...p, [brand.name]: !p[brand.name] }))}
              onClick={e => onBrandClick?.(brand.name, e)}
            />

            {/* Level 2: Assortments */}
            {!brandCollapsed && assortmentNames.map(assortName => {
              const assortKey   = `${brand.name}::${assortName}`;
              const assortSkus  = assortments[assortName] || [];
              const assortTY    = assortSkus.reduce((s, r) => s + (r.ty || 0), 0);
              const assortPY    = assortSkus.some(r => r.py != null)
                ? assortSkus.reduce((s, r) => s + (r.py || 0), 0) : null;
              const assortCollapsed = !expandedAssortments[assortKey];
              const assortSelected  = isActive(assortmentFilter, assortName);
              const assortDimmed    = isDimmedBy(assortmentFilter, assortName);
              const hasSkus = assortSkus.length > 0;
              const skuMaxVal = hasSkus ? Math.max(...assortSkus.map(s => s.ty || 0), 1) : 1;

              return (
                <div key={assortName} style={{ opacity: assortDimmed ? 0.3 : 1 }}>
                  {/* Level 2 row */}
                  <BarRow
                    name={assortName.replace(brand.name + ' ', '')}
                    ty={assortTY} py={assortPY} maxV={assortMaxVal}
                    indent={10} fontSize={10} fontWeight={600} color='var(--text-primary)'
                    selected={assortSelected}
                    hasChildren={hasSkus && assortSkus.length > 1} childrenCollapsed={assortCollapsed}
                    onToggle={() => setExpandedAssortments(p => ({ ...p, [assortKey]: !p[assortKey] }))}
                    onClick={e => onAssortmentClick?.(assortName, e)}
                  />

                  {/* Level 3: SKUs — show when assortment expanded OR only 1 SKU (always visible) */}
                  {(!assortCollapsed || assortSkus.length === 1) && assortSkus.map(sku => {
                    const isSkuSel = isActive(skuFilter, sku.name);
                    const isSkuDim = isDimmedBy(skuFilter, sku.name);
                    const sp       = skuMaxVal > 0 && sku.ty > 0 ? Math.min((sku.ty / skuMaxVal) * 100, 100) : 0;
                    const sdelta   = sku.py != null ? sku.ty - sku.py : null;
                    const sds      = fd(sdelta);
                    return (
                      <div key={sku.name}
                        style={{ paddingLeft: 20, paddingTop: 2, paddingBottom: 1, cursor: 'pointer',
                          opacity: isSkuDim ? 0.3 : 1, background: isSkuSel ? 'rgba(59,130,246,0.08)' : 'transparent', borderRadius: 3 }}
                        onClick={e => onSkuClick?.(sku.name, e)}>
                        <div style={{ fontSize: 9, color: isSkuSel ? 'var(--info)' : 'var(--text-body)', whiteSpace: 'normal', lineHeight: 1.3, marginBottom: 2 }}>
                          {sku.name}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <div style={{ flex: 1, height: 7, background: 'var(--border)', borderRadius: 2, minWidth: 20 }}>
                            {sku.ty > 0 && <div style={{ width: `${sp}%`, height: '100%', background: BOTH_COLOR, borderRadius: 2, opacity: 0.5, minWidth: 2 }} />}
                          </div>
                          <div style={{ width: AC_W, fontSize: 9, color: 'var(--text-primary)', textAlign: 'right', flexShrink: 0 }}>{sku.ty > 0 ? fv(sku.ty) : '—'}</div>
                          <div style={{ width: DPY_W, fontSize: 9, fontWeight: 600, textAlign: 'right', flexShrink: 0, color: sds == null ? 'var(--text-muted)' : sdelta >= 0 ? 'var(--success)' : 'var(--danger)' }}>{sds ?? '—'}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Totals row */}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{ width: 10, flexShrink: 0 }} />
        <div style={{ width: NAME_W, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>Total</div>
        {hasPY && <div style={{ width: PY_W, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>{totalPY != null ? fv(totalPY) : '—'}</div>}
        <div style={{ flex: 1 }} />
        <div style={{ width: AC_W, fontSize: 11, color: 'var(--text-primary)', textAlign: 'right', fontWeight: 600 }}>{fv(totalTY)}</div>
        <div style={{ width: DPY_W, fontSize: 11, fontWeight: 700, textAlign: 'right', color: totalPY == null ? 'var(--text-muted)' : (totalTY - totalPY) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
          {totalPY != null ? fd(totalTY - totalPY) : '—'}
        </div>
      </div>
    </div>
  );
}

// ── Week picker ───────────────────────────────────────────────────────────────
function WeekPicker({ weeks, selected, onChange, latestWeek }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const label = selected.length === 0 ? 'Select weeks…'
              : selected.length === 1 ? `Wk ${fmtWeekLabel(selected[0])}`
              : `${selected.length} weeks`;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{ background: 'var(--border)', border: '1px solid var(--border-soft)', color: 'var(--text-primary)', borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
        {label}<span style={{ fontSize: 10, color: 'var(--text-muted)' }}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, marginTop: 4, background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, maxHeight: 280, overflowY: 'auto', minWidth: 180, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
          <div style={{ display: 'flex', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--border-soft)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--info)', cursor: 'pointer' }} onClick={() => onChange([...weeks])}>All</span>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <span style={{ fontSize: 11, color: 'var(--info)', cursor: 'pointer' }} onClick={() => onChange(latestWeek ? [latestWeek] : [])}>Latest</span>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <span style={{ fontSize: 11, color: 'var(--info)', cursor: 'pointer' }} onClick={() => {
              const sorted = [...weeks].sort((a, b) => b.localeCompare(a));
              onChange(sorted.slice(0, 13));
            }}>L13W</span>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <span style={{ fontSize: 11, color: 'var(--info)', cursor: 'pointer' }} onClick={() => onChange([])}>Clear</span>
          </div>
          {weeks.map(w => (
            <label key={w} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', cursor: 'pointer' }}>
              <input type="checkbox" checked={selected.includes(w)} onChange={() => onChange(selected.includes(w) ? selected.filter(x => x !== w) : [...selected, w])} style={{ accentColor: 'var(--info)', cursor: 'pointer' }} />
              <span style={{ fontSize: 12, color: 'var(--text-primary)' }}>Wk {fmtWeekLabel(w)}{w === latestWeek ? <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>(latest)</span> : ''}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Filter badge ──────────────────────────────────────────────────────────────
function Badge({ label, value, onClear }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--info-soft)', borderRadius: 12, padding: '2px 10px', fontSize: 11, color: 'var(--info)' }}>
      {label && <span style={{ color: 'var(--text-muted)', marginRight: 2 }}>{label}:</span>}
      <span>{value}</span>
      <span style={{ cursor: 'pointer', color: 'var(--text-muted)', marginLeft: 4 }} onClick={onClear}>✕</span>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function StorePerformance() {
  const [measure,       setMeasure]       = useState('RSV');
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [storeData,     setStoreData]     = useState(null);
  const [brandData,     setBrandData]     = useState([]);
  // dimension filters
  const [retailerFilter,    setRetailerFilter]    = useState(null);
  const [stateFilter,       setStateFilter]       = useState(null);
  const [clusterFilter,     setClusterFilter]     = useState(null);
  const [sizeFilter,        setSizeFilter]        = useState(null);
  const [brandFilter,       setBrandFilter]       = useState(null);
  const [regionFilter,      setRegionFilter]      = useState(null);
  const [tourismFilter,     setTourismFilter]     = useState(null);
  const [gondolaFrontFilter,setGondolaFrontFilter]= useState(null);
  const [gondolaBackFilter, setGondolaBackFilter] = useState(null);
  const [ethnicFilter,      setEthnicFilter]      = useState(null);
  const [resortFilter,      setResortFilter]      = useState(null);
  const [nutBayFilter,      setNutBayFilter]      = useState(null);
  const [mpackBayFilter,    setMpackBayFilter]    = useState(null);
  const [groupzoneFilter,   setGroupzoneFilter]   = useState(null);
  const [subcategoryFilter, setSubcategoryFilter] = useState(null);
  const [assortmentFilter,  setAssortmentFilter]  = useState(null);
  const [skuFilter,         setSkuFilter]         = useState(null);
  // Per-store multi-select (Ctrl-click a bar in the Store panel to cross-filter
  // every other panel — state, cluster, brand, SKU etc. — to just those stores.
  // Stored as an array of "name|customer|number" keys so it survives FY vs weekly
  // views without depending on the computed displayName suffix.)
  const [storeFilter,       setStoreFilter]       = useState(null);

  // ── #6b: sliced loader ──────────────────────────────────────────────────────
  // The 12 MB monolith (previously a PUBLIC, no-auth static asset) was split into
  // authed JsonStore rows (scripts/split_store_performance.py). We load a ~0.8 MB
  // skeleton + only the selected week(s) on first paint, and lazy-load the ~5.7 MB
  // product-dimension maps only when a product filter is used. Pieces are cached in
  // refs and reassembled into the same `storeData` shape the rest of the page reads,
  // so nothing downstream changes (except totalWeeksAvail, which now reads the
  // skeleton's full weeks list rather than the count of loaded panels).
  const skeletonRef    = useRef(null);
  const weekCacheRef   = useRef({});    // { weekKey: { panel, rsv } }
  const productMapsRef = useRef(null);  // { sku_by_store, subcat_by_store, brand_by_store }
  const fetchingRef    = useRef(new Set());

  const reassemble = useCallback(() => {
    const sk = skeletonRef.current;
    if (!sk) return;
    const by_week = {};
    const store_rsv_by_week = {};
    for (const [w, slice] of Object.entries(weekCacheRef.current)) {
      if (slice?.panel) by_week[w] = slice.panel;
      for (const [storeKey, rsv] of Object.entries(slice?.rsv || {})) {
        (store_rsv_by_week[storeKey] ||= {})[w] = rsv;
      }
    }
    const pm = productMapsRef.current || {};
    setStoreData({
      ...sk,
      by_week,
      store_rsv_by_week,
      sku_by_store:    pm.sku_by_store    || {},
      subcat_by_store: pm.subcat_by_store || {},
      brand_by_store:  pm.brand_by_store  || {},
    });
  }, []);

  // Skeleton + brand reference (once).
  useEffect(() => {
    let alive = true;
    Promise.all([
      fetchData('store-performance').catch(() => null),
      fetchData('scanv2-brand.json').catch(() => []),
    ]).then(([sk, b]) => {
      if (!alive) return;
      setBrandData(b || []);
      if (sk) {
        skeletonRef.current = sk;
        reassemble();
        if (sk.latest_week) setSelectedWeeks([sk.latest_week]);
      }
    });
    return () => { alive = false; };
  }, [reassemble]);

  // Fetch the selected week slice(s) on demand; accumulate in the cache.
  useEffect(() => {
    if (!skeletonRef.current || selectedWeeks.length === 0) return;
    const missing = selectedWeeks.filter(
      (w) => !weekCacheRef.current[w] && !fetchingRef.current.has('wk:' + w));
    if (missing.length === 0) return;
    missing.forEach((w) => fetchingRef.current.add('wk:' + w));
    Promise.all(missing.map((w) =>
      fetchData(`store-perf-wk-${w}`)
        .then((slice) => ({ w, slice }))
        .catch(() => ({ w, slice: { panel: null, rsv: {} } })),
    )).then((results) => {
      for (const { w, slice } of results) {
        weekCacheRef.current[w] = slice;
        fetchingRef.current.delete('wk:' + w);
      }
      reassemble();
    });
  }, [selectedWeeks, reassemble]);

  // Lazy-load the product-dimension maps the first time a product filter is used.
  const wantProductMaps = !!(brandFilter || subcategoryFilter || assortmentFilter || skuFilter);
  useEffect(() => {
    if (!wantProductMaps || productMapsRef.current || fetchingRef.current.has('maps')) return;
    fetchingRef.current.add('maps');
    fetchData('store-perf-maps')
      .then((pm) => { productMapsRef.current = pm || {}; reassemble(); })
      .catch(() => { productMapsRef.current = {}; })
      .finally(() => fetchingRef.current.delete('maps'));
  }, [wantProductMaps, reassemble]);

  // CTRL+click = multi-select (adds to filter), plain click = single select (replaces)
  // v can be a single value or an array (e.g. zone-level click passes all child codes)
  function toggleFilter(setter) {
    return (v, e) => setter(prev => {
      const multi = e?.ctrlKey || e?.metaKey;
      const arr  = Array.isArray(prev) ? prev : (prev ? [prev] : []);
      const vals = Array.isArray(v) ? v : [v];   // normalise to array
      if (multi) {
        const allPresent = vals.every(x => arr.includes(x));
        const next = allPresent
          ? arr.filter(x => !vals.includes(x))
          : [...new Set([...arr, ...vals])];
        return next.length === 0 ? null : next;
      }
      // Single click: if current selection is exactly vals, clear; else set to vals
      const same = arr.length === vals.length && vals.every(x => arr.includes(x));
      return same ? null : vals;
    });
  }
  // Legacy single-toggle (for retailer card which stays single-select)
  function toggle(setter) { return v => setter(prev => prev === v ? null : v); }

  function clearAll() {
    setRetailerFilter(null); setStateFilter(null); setClusterFilter(null); setSizeFilter(null);
    setBrandFilter(null); setRegionFilter(null); setTourismFilter(null);
    setGondolaFrontFilter(null); setGondolaBackFilter(null); setEthnicFilter(null);
    setResortFilter(null); setNutBayFilter(null); setMpackBayFilter(null);
    setGroupzoneFilter(null); setSubcategoryFilter(null); setAssortmentFilter(null); setSkuFilter(null);
    setStoreFilter(null);
  }

  // ── Aggregate selected weeks ───────────────────────────────────────────────
  const byWeek    = storeData?.by_week || {};
  const weeksData = selectedWeeks.map(w => byWeek[w]).filter(Boolean);
  const ytd       = storeData?.ytd || {};
  const allStores     = storeData?.all_stores     || [];
  const storeAttrs    = storeData?.store_attrs    || {};
  // Per-store per-week RSV lookup: {"storeName|customer": {"weekKey": rsv_ty}}
  const storeRsvByWeek = storeData?.store_rsv_by_week || {};
  // Pre-sum selected weeks for each store (empty object = full year, use all_stores rsv_ty).
  // Use the skeleton's full weeks list (52) — with slicing, byWeek only holds the
  // weeks fetched so far, so counting loaded panels would wrongly make any subset
  // look like "all weeks" and disable date-filtering.
  const totalWeeksAvail = (storeData?.weeks || []).length || Object.keys(byWeek).length;
  const isDateFiltered  = selectedWeeks.length > 0 && selectedWeeks.length < totalWeeksAvail;
  const storeWeeklyRsv = (() => {
    if (!isDateFiltered || Object.keys(storeRsvByWeek).length === 0) return null;
    const computed = Object.fromEntries(
      Object.entries(storeRsvByWeek).map(([key, weekMap]) => [
        key,
        selectedWeeks.reduce((sum, w) => sum + (weekMap[w] || 0), 0),
      ])
    );
    // Only use if the selected period actually has store-level data — otherwise fall back to FY totals
    const hasData = Object.values(computed).some(v => v > 0);
    return hasData ? computed : null;
  })();
  const brandByStore    = storeData?.brand_by_store    || {};
  const subcatByStore   = storeData?.subcat_by_store   || {};
  const skuByStore      = storeData?.sku_by_store      || {};
  const skuSubcategory  = storeData?.sku_subcategory   || {};
  const hasBrandByStore  = Object.keys(brandByStore).length  > 0;
  const hasSubcatByStore = Object.keys(subcatByStore).length > 0;
  const hasSkuByStore    = Object.keys(skuByStore).length    > 0;

  // Derive assortment → store lookup from sku_by_store (computed, no rebuild needed)
  const assortmentByStore = (() => {
    if (!hasSkuByStore) return {};
    const result = {};
    // skuByStore keys are now "storeName|storeNumber" — preserve those keys directly
    for (const [storeKey, skus] of Object.entries(skuByStore)) {
      for (const [skuName, vals] of Object.entries(skus)) {
        const assortment = deriveAssortment(skuName);
        if (!result[storeKey]) result[storeKey] = {};
        if (!result[storeKey][assortment]) result[storeKey][assortment] = { ww_ty: 0, ww_py: null, coles_ty: 0, coles_py: null, ww_units_ty: 0, coles_units_ty: 0 };
        const e = result[storeKey][assortment];
        e.ww_ty += vals.ww_ty || 0;  e.ww_units_ty += vals.ww_units_ty || 0;
        e.coles_ty += vals.coles_ty || 0;  e.coles_units_ty += vals.coles_units_ty || 0;
        if (vals.ww_py    != null) e.ww_py    = (e.ww_py    || 0) + vals.ww_py;
        if (vals.coles_py != null) e.coles_py = (e.coles_py || 0) + vals.coles_py;
      }
    }
    return result;
  })();
  const hasAssortmentByStore = Object.keys(assortmentByStore).length > 0;

  // ── Per-week helpers ───────────────────────────────────────────────────────
  const hasWeeklyPanels = weeksData.length > 0 && weeksData[0]?.gondola_front != null;

  function weeklyOrYTD(perWeekKey, ytdKey) {
    if (hasWeeklyPanels) return sumWeeks(weeksData, perWeekKey);
    return ytd[ytdKey] || [];
  }

  function periodNote(extra = '') {
    if (!hasWeeklyPanels) return `YTD${extra ? ' · ' + extra : ''}`;
    if (selectedWeeks.length === 1) return `Wk ${fmtWeekLabel(selectedWeeks[0])}${extra ? ' · ' + extra : ''}`;
    if (selectedWeeks.length === weeksData.length && weeksData.length > 1) return `FY YTD${extra ? ' · ' + extra : ''}`;
    return `${selectedWeeks.length} wks${extra ? ' · ' + extra : ''}`;
  }

  // ── Cross-filter: derive any panel from all_stores when filters are active ─
  // Product-dimension lookups (brand_by_store, subcat_by_store, etc.) are FY-only — there is
  // no per-week variant. When a product filter is active the derive path must be used regardless
  // of week selection; the FY · filtered label makes this clear to the user.
  // Geographic-only filters respect week selection: canDerive is suppressed when per-week store
  // data is available so that weekly totals are not inflated by FY all_stores values.
  const hasProductFilter = !!(
    (brandFilter && hasBrandByStore) || (subcategoryFilter && hasSubcatByStore) ||
    (assortmentFilter && hasAssortmentByStore) || (skuFilter && hasSkuByStore));
  const hasGeoFilter = !!(stateFilter || clusterFilter || sizeFilter ||
    gondolaFrontFilter || gondolaBackFilter || ethnicFilter || resortFilter ||
    nutBayFilter || mpackBayFilter || groupzoneFilter || regionFilter || tourismFilter);
  const hasAnyFilter = hasProductFilter || hasGeoFilter;
  // Allow canDerive when product filters are active (FY data required); for geo-only filters
  // suppress when per-week store data exists to avoid inflating weekly totals with FY figures.
  const canDerive = hasAnyFilter && allStores.length > 0;

  const rf = retailerFilter;   // 'ww' | 'coles' | null

  // Weekly scale factor: when a product filter is active + a specific week is selected,
  // scale FY store-level product values to weekly estimates (weeklyRsv / fyRsv).
  function getWkScale(s) {
    if (!isDateFiltered || !storeWeeklyRsv) return 1;
    const wkRsv = storeWeeklyRsv[s.name + '|' + (s.number || '') + '|' + (s.customer || '')] || 0;
    const fyRsv = s.rsv_ty || 0;
    return fyRsv > 0 ? wkRsv / fyRsv : 0;
  }

  // deriveRows: groups all_stores by groupBy, applying all active filters except skipKey.
  // groupBy: 'state'|'cluster'|'size'|'resort_display'|store_attr_key
  // skipKey: the filter belonging to the current panel (so all values remain visible)
  const storeFilterSet = storeFilter ? new Set([].concat(storeFilter)) : null;
  function deriveRows(groupBy, skipKey) {
    const agg = {};
    for (const s of allStores) {
      if (rf === 'ww'    && s.customer !== 'ww')    continue;
      if (rf === 'coles' && s.customer !== 'coles') continue;
      // Cross-filter: if the user multi-selected specific stores on the Store
      // panel, every other panel only sums those stores' rsv.
      if (storeFilterSet) {
        const sk = (s.name || '') + '|' + (s.customer || '') + '|' + (s.number || '');
        if (!storeFilterSet.has(sk)) continue;
      }
      const a = storeAttrs[s.name + '|' + (s.number || '')] || {};
      if (skipKey !== 'state'         && !matchFilter(stateFilter,        s.state))        continue;
      if (skipKey !== 'cluster'       && !matchFilter(clusterFilter,      s.cluster))      continue;
      if (skipKey !== 'size'          && !matchFilter(sizeFilter,         s.size))         continue;
      if (skipKey !== 'region'        && !matchFilter(regionFilter,       a.region))       continue;
      if (skipKey !== 'tourism'       && !matchFilter(tourismFilter,      a.tourism))      continue;
      if (skipKey !== 'gondola_front' && !matchFilter(gondolaFrontFilter, a.gondola_front)) continue;
      if (skipKey !== 'gondola_back'  && !matchFilter(gondolaBackFilter,  a.gondola_back))  continue;
      if (skipKey !== 'ethnic'        && !matchFilter(ethnicFilter,       a.ethnic))       continue;
      if (skipKey !== 'groupzone'     && !matchFilter(groupzoneFilter,    a.groupzone))    continue;
      if (skipKey !== 'resort' && resortFilter) {
        const exp = [].concat(resortFilter).map(v => v === 'Resort' ? 'Y' : 'N');
        if (!exp.includes(a.resort)) continue;
      }
      if (skipKey !== 'nut_bay' && nutBayFilter) {
        const vals = [].concat(nutBayFilter);
        if (!vals.some(v => parseFloat(a.nut_bay) === parseFloat(v))) continue;
      }
      if (skipKey !== 'mpack_bay' && mpackBayFilter) {
        const vals = [].concat(mpackBayFilter);
        if (!vals.some(v => parseFloat(a.multipacks_bay) === parseFloat(v))) continue;
      }
      // Product dimension filters — check via store-level product lookup
      if (skipKey !== 'brand' && brandFilter) {
        const names = [].concat(brandFilter);
        const sb = brandByStore[s.name + '|' + (s.number || '')] || {};
        if (!names.some(b => sb[b] != null)) continue;
      }
      if (skipKey !== 'subcategory' && subcategoryFilter) {
        const names = [].concat(subcategoryFilter);
        const ss = subcatByStore[s.name + '|' + (s.number || '')] || {};
        if (!names.some(b => ss[b] != null)) continue;
      }
      let val;
      if      (groupBy === 'state')           val = s.state;
      else if (groupBy === 'cluster')         val = s.cluster;
      else if (groupBy === 'size')            val = s.size;
      else if (groupBy === 'resort_display')  val = a.resort === 'Y' ? 'Resort' : a.resort === 'N' ? 'Non-Resort' : null;
      else                                    val = a[groupBy];
      if (!val) continue;
      if (!agg[val]) agg[val] = { name: val, ww_ty: 0, ww_py: null, coles_ty: 0, coles_py: null, ww_units_ty: 0, coles_units_ty: 0, ww_units_py: null, coles_units_py: null };
      const e = agg[val];
      // SKU or assortment filter: aggregate that product's sales from per-store lookup
      if (skipKey !== 'sku' && skuFilter && hasSkuByStore) {
        const scale = getWkScale(s);
        const selectedNames = [].concat(skuFilter);
        const storeSkus = skuByStore[s.name + '|' + (s.number || '')] || {};
        for (const skuName of selectedNames) {
          const sv = storeSkus[skuName];
          if (!sv) continue;
          if (s.customer === 'ww') {
            e.ww_ty += (sv.ww_ty || 0) * scale;  e.ww_units_ty += (sv.ww_units_ty || 0) * scale;
            if (sv.ww_py != null) e.ww_py = (e.ww_py || 0) + sv.ww_py * scale;
          } else {
            e.coles_ty += (sv.coles_ty || 0) * scale;  e.coles_units_ty += (sv.coles_units_ty || 0) * scale;
            if (sv.coles_py != null) e.coles_py = (e.coles_py || 0) + sv.coles_py * scale;
          }
        }
      } else if (skipKey !== 'assortment' && assortmentFilter && hasAssortmentByStore) {
        const scale = getWkScale(s);
        const selectedNames = [].concat(assortmentFilter);
        const storeAssortments = assortmentByStore[s.name + '|' + (s.number || '')] || {};
        for (const assortName of selectedNames) {
          const sv = storeAssortments[assortName];
          if (!sv) continue;
          if (s.customer === 'ww') {
            e.ww_ty += (sv.ww_ty || 0) * scale;  e.ww_units_ty += (sv.ww_units_ty || 0) * scale;
            if (sv.ww_py != null) e.ww_py = (e.ww_py || 0) + sv.ww_py * scale;
          } else {
            e.coles_ty += (sv.coles_ty || 0) * scale;  e.coles_units_ty += (sv.coles_units_ty || 0) * scale;
            if (sv.coles_py != null) e.coles_py = (e.coles_py || 0) + sv.coles_py * scale;
          }
        }
      } else if (skipKey !== 'brand' && brandFilter && hasBrandByStore) {
        const scale = getWkScale(s);
        const selectedNames = [].concat(brandFilter);
        const storeBrands = brandByStore[s.name + '|' + (s.number || '')] || {};
        for (const brandName of selectedNames) {
          const sv = storeBrands[brandName];
          if (!sv) continue;
          if (s.customer === 'ww') {
            e.ww_ty += (sv.ww_ty || 0) * scale;  e.ww_units_ty += (sv.ww_units_ty || 0) * scale;
            if (sv.ww_py != null) e.ww_py = (e.ww_py || 0) + sv.ww_py * scale;
          } else {
            e.coles_ty += (sv.coles_ty || 0) * scale;  e.coles_units_ty += (sv.coles_units_ty || 0) * scale;
            if (sv.coles_py != null) e.coles_py = (e.coles_py || 0) + sv.coles_py * scale;
          }
        }
      } else if (skipKey !== 'subcategory' && subcategoryFilter && hasSubcatByStore) {
        const scale = getWkScale(s);
        const selectedNames = [].concat(subcategoryFilter);
        const storeSubcats = subcatByStore[s.name + '|' + (s.number || '')] || {};
        for (const subcatName of selectedNames) {
          const sv = storeSubcats[subcatName];
          if (!sv) continue;
          if (s.customer === 'ww') {
            e.ww_ty += (sv.ww_ty || 0) * scale;  e.ww_units_ty += (sv.ww_units_ty || 0) * scale;
            if (sv.ww_py != null) e.ww_py = (e.ww_py || 0) + sv.ww_py * scale;
          } else {
            e.coles_ty += (sv.coles_ty || 0) * scale;  e.coles_units_ty += (sv.coles_units_ty || 0) * scale;
            if (sv.coles_py != null) e.coles_py = (e.coles_py || 0) + sv.coles_py * scale;
          }
        }
      } else {
        // Normal mode: use total store RSV, scaled to selected period
        const scale = getWkScale(s);
        if (s.customer === 'ww') {
          e.ww_ty += (s.rsv_ty || 0) * scale;  e.ww_units_ty += (s.units_ty || 0) * scale;
          if (s.rsv_py   != null) e.ww_py       = (e.ww_py       || 0) + s.rsv_py   * scale;
          if (s.units_py != null) e.ww_units_py = (e.ww_units_py || 0) + s.units_py * scale;
        } else {
          e.coles_ty += (s.rsv_ty || 0) * scale;  e.coles_units_ty += (s.units_ty || 0) * scale;
          if (s.rsv_py   != null) e.coles_py       = (e.coles_py       || 0) + s.rsv_py   * scale;
          if (s.units_py != null) e.coles_units_py = (e.coles_units_py || 0) + s.units_py * scale;
        }
      }
    }
    // Remove zero-value entries (stores with no SKU data in that group)
    return Object.values(agg).filter(r => r.ww_ty > 0 || r.coles_ty > 0);
  }

  // Derive brand or subcategory panel from store-level product breakdown.
  // crossLookup/crossFilter: optional other-dimension filter (e.g. when deriving brand rows,
  // pass subcatByStore + subcategoryFilter to restrict to stores in that subcategory).
  // skipSku: pass true when deriving the SKU panel itself (don't self-filter)
  function deriveProductRows(productByStore, crossLookup, crossFilter, skipSku = false) {
    const agg = {};
    for (const s of allStores) {
      if (rf === 'ww'    && s.customer !== 'ww')    continue;
      if (rf === 'coles' && s.customer !== 'coles') continue;
      const a = storeAttrs[s.name + '|' + (s.number || '')] || {};
      // Apply all store-level filters
      if (!matchFilter(stateFilter,        s.state))        continue;
      if (!matchFilter(clusterFilter,      s.cluster))      continue;
      if (!matchFilter(sizeFilter,         s.size))         continue;
      if (!matchFilter(regionFilter,       a.region))       continue;
      if (!matchFilter(tourismFilter,      a.tourism))      continue;
      if (!matchFilter(gondolaFrontFilter, a.gondola_front)) continue;
      if (!matchFilter(gondolaBackFilter,  a.gondola_back))  continue;
      if (!matchFilter(ethnicFilter,       a.ethnic))       continue;
      if (!matchFilter(groupzoneFilter,    a.groupzone))    continue;
      if (resortFilter) { const exp = [].concat(resortFilter).map(v => v === 'Resort' ? 'Y' : 'N'); if (!exp.includes(a.resort)) continue; }
      if (nutBayFilter)  { const vals = [].concat(nutBayFilter);  if (!vals.some(v => parseFloat(a.nut_bay) === parseFloat(v))) continue; }
      if (mpackBayFilter){ const vals = [].concat(mpackBayFilter); if (!vals.some(v => parseFloat(a.multipacks_bay) === parseFloat(v))) continue; }
      // Apply cross-dimension filter using a separate lookup (e.g. subcatByStore when deriving brand rows)
      if (crossFilter && crossLookup) {
        const names = [].concat(crossFilter);
        if (names.length) {
          const lookup = crossLookup[s.name + '|' + (s.number || '')] || {};
          if (!names.some(b => lookup[b] != null)) continue;
        }
      }
      // Restrict to stores carrying the selected SKU / assortment
      if (skuFilter && hasSkuByStore) {
        const names = [].concat(skuFilter);
        const ss = skuByStore[s.name + '|' + (s.number || '')] || {};
        if (!names.some(b => ss[b] != null)) continue;
      } else if (assortmentFilter && hasAssortmentByStore) {
        const names = [].concat(assortmentFilter);
        const ss = assortmentByStore[s.name + '|' + (s.number || '')] || {};
        if (!names.some(b => ss[b] != null)) continue;
      }
      // Sum this store's product breakdown (scale to weekly when date-filtered + product filter)
      const scale = getWkScale(s);
      for (const [prodName, vals] of Object.entries(productByStore[s.name + '|' + (s.number || '')] || {})) {
        if (!agg[prodName]) agg[prodName] = { name: prodName, ww_ty: 0, ww_py: null, coles_ty: 0, coles_py: null, ww_units_ty: 0, coles_units_ty: 0 };
        const e = agg[prodName];
        if (s.customer === 'ww') {
          e.ww_ty += (vals.ww_ty || 0) * scale;  e.ww_units_ty += (vals.ww_units_ty || 0) * scale;
          if (vals.ww_py != null) e.ww_py = (e.ww_py || 0) + vals.ww_py * scale;
        } else {
          e.coles_ty += (vals.coles_ty || 0) * scale;  e.coles_units_ty += (vals.coles_units_ty || 0) * scale;
          if (vals.coles_py != null) e.coles_py = (e.coles_py || 0) + vals.coles_py * scale;
        }
      }
    }
    return Object.values(agg);
  }

  // Only the product-filter path uses FY-totals × week-share scaling (via
  // getWkScale), so only that combination is actually an estimate. Geographic
  // filters on a selected week still use real store-level weekly RSV and are
  // accurate — label them plainly as "Week · filtered" rather than "Wk est.".
  const derivedNote = canDerive
    ? (
        hasProductFilter && isDateFiltered && storeWeeklyRsv
          ? 'Wk est. · filtered'
          : isDateFiltered && storeWeeklyRsv
            ? 'Week · filtered'
            : 'FY · filtered'
      )
    : null;

  // ── Retailer base ──────────────────────────────────────────────────────────
  const retailerBase = (() => {
    if (canDerive) {
      const ww    = { name: 'Woolworths Limited', ww_ty: 0, ww_py: null, coles_ty: 0, coles_py: null, ww_units_ty: 0, coles_units_ty: 0 };
      const coles = { name: 'Coles Supermarkets', ww_ty: 0, ww_py: null, coles_ty: 0, coles_py: null, ww_units_ty: 0, coles_units_ty: 0 };
      for (const s of allStores) {
        const a = storeAttrs[s.name + '|' + (s.number || '')] || {};
        if (!matchFilter(stateFilter, s.state)) continue;
        if (!matchFilter(clusterFilter, s.cluster)) continue;
        if (!matchFilter(sizeFilter, s.size)) continue;
        if (!matchFilter(regionFilter, a.region)) continue;
        if (!matchFilter(tourismFilter, a.tourism)) continue;
        if (!matchFilter(gondolaFrontFilter, a.gondola_front)) continue;
        if (!matchFilter(gondolaBackFilter, a.gondola_back)) continue;
        if (!matchFilter(ethnicFilter, a.ethnic)) continue;
        if (!matchFilter(groupzoneFilter, a.groupzone)) continue;
        if (resortFilter) { const exp = [].concat(resortFilter).map(v => v === 'Resort' ? 'Y' : 'N'); if (!exp.includes(a.resort)) continue; }
        if (nutBayFilter)  { const vals = [].concat(nutBayFilter);  if (!vals.some(v => parseFloat(a.nut_bay) === parseFloat(v))) continue; }
        if (mpackBayFilter){ const vals = [].concat(mpackBayFilter); if (!vals.some(v => parseFloat(a.multipacks_bay) === parseFloat(v))) continue; }
        // Product dimension filters — presence check (same as deriveRows)
        if (brandFilter) {
          const names = [].concat(brandFilter);
          const sb = brandByStore[s.name + '|' + (s.number || '')] || {};
          if (!names.some(b => sb[b] != null)) continue;
        }
        if (subcategoryFilter) {
          const names = [].concat(subcategoryFilter);
          const ss = subcatByStore[s.name + '|' + (s.number || '')] || {};
          if (!names.some(b => ss[b] != null)) continue;
        }
        // When SKU or assortment filter active, aggregate product-level RSV not total store RSV
        if (skuFilter && hasSkuByStore) {
          const scale = getWkScale(s);
          const selectedNames = [].concat(skuFilter);
          const storeSkus = skuByStore[s.name + '|' + (s.number || '')] || {};
          for (const skuName of selectedNames) {
            const sv = storeSkus[skuName];
            if (!sv) continue;
            if (s.customer === 'ww') {
              ww.ww_ty += (sv.ww_ty || 0) * scale;  ww.ww_units_ty += (sv.ww_units_ty || 0) * scale;
              if (sv.ww_py != null) ww.ww_py = (ww.ww_py || 0) + sv.ww_py * scale;
            } else {
              coles.coles_ty += (sv.coles_ty || 0) * scale;  coles.coles_units_ty += (sv.coles_units_ty || 0) * scale;
              if (sv.coles_py != null) coles.coles_py = (coles.coles_py || 0) + sv.coles_py * scale;
            }
          }
        } else if (assortmentFilter && hasAssortmentByStore) {
          const scale = getWkScale(s);
          const selectedNames = [].concat(assortmentFilter);
          const storeAssortments = assortmentByStore[s.name + '|' + (s.number || '')] || {};
          for (const assortName of selectedNames) {
            const sv = storeAssortments[assortName];
            if (!sv) continue;
            if (s.customer === 'ww') {
              ww.ww_ty += (sv.ww_ty || 0) * scale;  ww.ww_units_ty += (sv.ww_units_ty || 0) * scale;
              if (sv.ww_py != null) ww.ww_py = (ww.ww_py || 0) + sv.ww_py * scale;
            } else {
              coles.coles_ty += (sv.coles_ty || 0) * scale;  coles.coles_units_ty += (sv.coles_units_ty || 0) * scale;
              if (sv.coles_py != null) coles.coles_py = (coles.coles_py || 0) + sv.coles_py * scale;
            }
          }
        } else if (brandFilter && hasBrandByStore) {
          const scale = getWkScale(s);
          const selectedNames = [].concat(brandFilter);
          const storeBrands = brandByStore[s.name + '|' + (s.number || '')] || {};
          for (const brandName of selectedNames) {
            const sv = storeBrands[brandName];
            if (!sv) continue;
            if (s.customer === 'ww') {
              ww.ww_ty += (sv.ww_ty || 0) * scale;  ww.ww_units_ty += (sv.ww_units_ty || 0) * scale;
              if (sv.ww_py != null) ww.ww_py = (ww.ww_py || 0) + sv.ww_py * scale;
            } else {
              coles.coles_ty += (sv.coles_ty || 0) * scale;  coles.coles_units_ty += (sv.coles_units_ty || 0) * scale;
              if (sv.coles_py != null) coles.coles_py = (coles.coles_py || 0) + sv.coles_py * scale;
            }
          }
        } else if (subcategoryFilter && hasSubcatByStore) {
          const scale = getWkScale(s);
          const selectedNames = [].concat(subcategoryFilter);
          const storeSubcats = subcatByStore[s.name + '|' + (s.number || '')] || {};
          for (const subcatName of selectedNames) {
            const sv = storeSubcats[subcatName];
            if (!sv) continue;
            if (s.customer === 'ww') {
              ww.ww_ty += (sv.ww_ty || 0) * scale;  ww.ww_units_ty += (sv.ww_units_ty || 0) * scale;
              if (sv.ww_py != null) ww.ww_py = (ww.ww_py || 0) + sv.ww_py * scale;
            } else {
              coles.coles_ty += (sv.coles_ty || 0) * scale;  coles.coles_units_ty += (sv.coles_units_ty || 0) * scale;
              if (sv.coles_py != null) coles.coles_py = (coles.coles_py || 0) + sv.coles_py * scale;
            }
          }
        } else {
          const scale = getWkScale(s);
          if (s.customer === 'ww') {
            ww.ww_ty += (s.rsv_ty || 0) * scale;  ww.ww_units_ty += (s.units_ty || 0) * scale;
            if (s.rsv_py != null) ww.ww_py = (ww.ww_py || 0) + s.rsv_py * scale;
          } else {
            coles.coles_ty += (s.rsv_ty || 0) * scale;  coles.coles_units_ty += (s.units_ty || 0) * scale;
            if (s.rsv_py != null) coles.coles_py = (coles.coles_py || 0) + s.rsv_py * scale;
          }
        }
      }
      return [ww, coles];
    }
    if (!stateFilter) return sumWeeks(weeksData, 'retailers');
    const stateArr = Array.isArray(stateFilter) ? stateFilter : [stateFilter];
    const weekly = sumWeeks(weeksData, 'states');
    const matching = weekly.filter(s => stateArr.includes(s.name));
    if (!matching.length) return [];
    return [
      { name: 'Woolworths Limited', ww_ty: matching.reduce((s,r)=>s+(r.ww_ty||0),0), ww_py: matching.some(r=>r.ww_py!=null)?matching.reduce((s,r)=>s+(r.ww_py||0),0):null, coles_ty: 0, coles_py: null },
      { name: 'Coles Supermarkets', ww_ty: 0, ww_py: null, coles_ty: matching.reduce((s,r)=>s+(r.coles_ty||0),0), coles_py: matching.some(r=>r.coles_py!=null)?matching.reduce((s,r)=>s+(r.coles_py||0),0):null },
    ];
  })();

  // ── Display rows ───────────────────────────────────────────────────────────
  const retailerDisplayRows = toDisplay(retailerBase, rf, measure);

  const stateRows    = toDisplay(canDerive ? deriveRows('state',   'state')   : sumWeeks(weeksData, 'states'),   rf, measure);
  const clusterRaw   = canDerive ? deriveRows('cluster', 'cluster') : (stateFilter ? sumWeeksByState(weeksData, 'clusters_by_state', stateFilter) : sumWeeks(weeksData, 'clusters'));
  const clusterRows  = toDisplay(clusterRaw, rf, measure);
  const sizeRows     = toDisplay(canDerive ? deriveRows('size',    'size')    : (stateFilter ? sumWeeksByState(weeksData, 'sizes_by_state', stateFilter) : sumWeeks(weeksData, 'sizes')),    rf, measure);

  const regionRows  = toDisplay(canDerive ? deriveRows('region',  'region')  : (() => { const stateArr = stateFilter ? [].concat(stateFilter) : null; const rows = weeklyOrYTD('regions', 'regions'); return stateArr ? rows.filter(r => stateArr.includes(REGION_STATE[r.name])) : rows; })(), rf, measure);
  const tourismRows = toDisplay(canDerive ? deriveRows('tourism', 'tourism') : (() => { const stateArr = stateFilter ? [].concat(stateFilter) : null; const rows = weeklyOrYTD('tourism', 'tourism'); return stateArr ? rows.filter(r => stateArr.includes(TOURISM_STATE[r.name])) : rows; })(), rf, measure);

  // WW-only panels
  const showWW = rf !== 'coles';
  const wwOnly = r => ({ ...r, coles_ty: 0, coles_py: null, coles_units_ty: 0, coles_units_py: null });
  const mapResort = rows => rows.map(r => ({ ...r, name: r.name === 'Y' ? 'Resort' : r.name === 'N' ? 'Non-Resort' : r.name }));

  const gondolaFront = showWW ? toDisplay(canDerive ? deriveRows('gondola_front', 'gondola_front') : weeklyOrYTD('gondola_front', 'ww_front_gondola').map(wwOnly), null, measure) : [];
  const gondolaBack  = showWW ? toDisplay(canDerive ? deriveRows('gondola_back',  'gondola_back')  : weeklyOrYTD('gondola_back',  'ww_back_gondola').map(wwOnly),  null, measure) : [];
  const ethnicTiers  = showWW ? toDisplay(canDerive ? deriveRows('ethnic',        'ethnic')        : weeklyOrYTD('ethnic_tiers',  'ww_ethnic_tiers').map(wwOnly),  null, measure) : [];
  const resortRows   = showWW ? toDisplay(canDerive ? deriveRows('resort_display','resort')        : mapResort(weeklyOrYTD('resort', 'ww_resort').map(wwOnly)),    null, measure) : [];
  const nutBayRows   = toDisplay(canDerive ? deriveRows('nut_bay',       'nut_bay')   : weeklyOrYTD('nut_bay',        'nut_bay_size'),        rf, measure);
  const mpackBayRows = toDisplay(canDerive ? deriveRows('multipacks_bay','mpack_bay') : weeklyOrYTD('multipacks_bay', 'multipacks_bay_size'), rf, measure);
  // Groupzone / subcategory may be absent from the latest partial week — fall back to all weeks
  const allByWeekValues = Object.values(byWeek);
  function sumWeeksWithFallback(key) {
    const rows = sumWeeks(weeksData, key);
    if (rows.length > 0) return rows;
    return sumWeeks(allByWeekValues, key);
  }
  const groupzoneRaw   = canDerive ? deriveRows('groupzone', 'groupzone') : sumWeeksWithFallback('groupzone');
  const groupzoneRows  = toDisplay(groupzoneRaw, rf, measure);

  // Brand — derived from store-brand breakdown when any filter active; else per-week / SCAN YTD
  const hasWeeklyBrand = weeksData.length > 0 && weeksData[0]?.brands != null;

  // SKU breakdown per brand — aggregate across selected weeks, apply retailer filter
  const skusByBrand = (() => {
    const agg = {};
    // Fall back to all weeks when no selected week has brand_skus (e.g. latest partial week)
    const sources = weeksData.some(wd => wd.brand_skus) ? weeksData : Object.values(byWeek);
    for (const wd of sources) {
      for (const [brand, skus] of Object.entries(wd.brand_skus || {})) {
        for (const sku of skus) {
          if (!agg[brand]) agg[brand] = {};
          if (!agg[brand][sku.name]) agg[brand][sku.name] = { name: sku.name, ww_ty: 0, ww_py: null, coles_ty: 0, coles_py: null };
          const e = agg[brand][sku.name];
          e.ww_ty    += sku.ww_ty    || 0;
          e.coles_ty += sku.coles_ty || 0;
          if (sku.ww_py    != null) e.ww_py    = (e.ww_py    || 0) + sku.ww_py;
          if (sku.coles_py != null) e.coles_py = (e.coles_py || 0) + sku.coles_py;
        }
      }
    }
    const result = {};
    for (const [brand, skuMap] of Object.entries(agg)) {
      const skuArr = Object.values(skuMap).map(s => {
        const ty = rf === 'coles' ? s.coles_ty : rf === 'ww' ? s.ww_ty : (s.ww_ty || 0) + (s.coles_ty || 0);
        const py = rf === 'coles' ? s.coles_py : rf === 'ww' ? s.ww_py
          : (s.ww_py != null || s.coles_py != null) ? (s.ww_py || 0) + (s.coles_py || 0) : null;
        return { name: s.name, ty, py };
      }).filter(s => s.ty > 0).sort((a, b) => b.ty - a.ty);
      if (skuArr.length > 0) result[brand] = skuArr;
    }
    return result;
  })();

  // Build reverse maps and assortment hierarchy from skusByBrand
  const skuToBrand = {};
  const skuToAssortment = {};
  const assortmentsByBrand = {};   // {brand: {assortment: [{name, ty, py}]}}
  for (const [brand, skus] of Object.entries(skusByBrand)) {
    assortmentsByBrand[brand] = {};
    for (const sku of skus) {
      skuToBrand[sku.name] = brand;
      const assortment = deriveAssortment(sku.name);
      skuToAssortment[sku.name] = assortment;
      if (!assortmentsByBrand[brand][assortment]) assortmentsByBrand[brand][assortment] = [];
      assortmentsByBrand[brand][assortment].push(sku);
    }
  }
  // Normalize brand names from weekly panel to uppercase to deduplicate "BHUJA"/"Bhuja"
  function normalizeBrandRows(rows) {
    const agg = {};
    for (const r of rows) {
      const name = normalizeBrand(r.name);
      if (!agg[name]) agg[name] = { ...r, name };
      else { agg[name].ty += r.ty || 0; if (r.py != null) agg[name].py = (agg[name].py || 0) + r.py; }
    }
    return Object.values(agg);
  }

  // Generic helper: aggregate productLookup by brand (via brandOf map) for selected product names
  function deriveBrandRowsFromProductLookup(productLookup, selectedNames, brandOf) {
    const agg = {};
    for (const s of allStores) {
      if (rf === 'ww' && s.customer !== 'ww') continue;
      if (rf === 'coles' && s.customer !== 'coles') continue;
      const a = storeAttrs[s.name + '|' + (s.number || '')] || {};
      if (!matchFilter(stateFilter, s.state)) continue;
      if (!matchFilter(clusterFilter, s.cluster)) continue;
      if (!matchFilter(sizeFilter, s.size)) continue;
      if (!matchFilter(regionFilter, a.region)) continue;
      if (!matchFilter(tourismFilter, a.tourism)) continue;
      if (!matchFilter(gondolaFrontFilter, a.gondola_front)) continue;
      if (!matchFilter(gondolaBackFilter, a.gondola_back)) continue;
      if (!matchFilter(ethnicFilter, a.ethnic)) continue;
      if (!matchFilter(groupzoneFilter, a.groupzone)) continue;
      if (resortFilter) { const exp = [].concat(resortFilter).map(v => v === 'Resort' ? 'Y' : 'N'); if (!exp.includes(a.resort)) continue; }
      if (nutBayFilter)  { const vals = [].concat(nutBayFilter);  if (!vals.some(v => parseFloat(a.nut_bay) === parseFloat(v))) continue; }
      if (mpackBayFilter){ const vals = [].concat(mpackBayFilter); if (!vals.some(v => parseFloat(a.multipacks_bay) === parseFloat(v))) continue; }
      if (subcategoryFilter && hasSubcatByStore) { const names = [].concat(subcategoryFilter); const ss = subcatByStore[s.name + '|' + (s.number || '')] || {}; if (!names.some(b => ss[b] != null)) continue; }
      const scale = getWkScale(s);
      const storeLookup = productLookup[s.name + '|' + (s.number || '')] || {};
      for (const prodName of selectedNames) {
        const sv = storeLookup[prodName];
        if (!sv) continue;
        const brand = brandOf[prodName] || 'OTHER';
        if (!agg[brand]) agg[brand] = { name: brand, ww_ty: 0, ww_py: null, coles_ty: 0, coles_py: null, ww_units_ty: 0, coles_units_ty: 0 };
        const e = agg[brand];
        if (s.customer === 'ww') {
          e.ww_ty += (sv.ww_ty || 0) * scale;  e.ww_units_ty += (sv.ww_units_ty || 0) * scale;
          if (sv.ww_py != null) e.ww_py = (e.ww_py || 0) + sv.ww_py * scale;
        } else {
          e.coles_ty += (sv.coles_ty || 0) * scale;  e.coles_units_ty += (sv.coles_units_ty || 0) * scale;
          if (sv.coles_py != null) e.coles_py = (e.coles_py || 0) + sv.coles_py * scale;
        }
      }
    }
    return Object.values(agg);
  }

  const brandRows = (canDerive && skuFilter && hasSkuByStore)
    ? toDisplay(deriveBrandRowsFromProductLookup(skuByStore, [].concat(skuFilter), skuToBrand), rf, measure)
    : (canDerive && assortmentFilter && hasAssortmentByStore)
    ? toDisplay(deriveBrandRowsFromProductLookup(assortmentByStore, [].concat(assortmentFilter), Object.fromEntries(Object.entries(assortmentsByBrand).flatMap(([b, assorts]) => Object.keys(assorts).map(a => [a, b])))), rf, measure)
    : (canDerive && hasBrandByStore)
    ? toDisplay(normalizeBrandRows(deriveProductRows(brandByStore, hasSubcatByStore ? subcatByStore : null, subcategoryFilter)), rf, measure)
    : hasWeeklyBrand
      ? normalizeBrandRows(toDisplay(sumWeeks(weeksData, 'brands'), rf, measure))
      : brandData.map(b => {
          const ty = rf === 'coles' ? b.coles_ytd_ty : rf === 'ww' ? b.ww_ytd_ty : (b.ww_ytd_ty || 0) + (b.coles_ytd_ty || 0);
          const py = rf === 'coles' ? b.coles_ytd_ly : rf === 'ww' ? b.ww_ytd_ly : (b.ww_ytd_ly || 0) + (b.coles_ytd_ly || 0);
          return { name: b.family, ty, py };
        }).filter(r => r.ty > 0);

  // Subcategory — when SKU/assortment/brand filter active, derive from skuByStore+skuSubcategory so
  // only subcategories belonging to the selected products are shown (not all-product totals).
  const subcategoryRows = (() => {
    if (canDerive && hasSubcatByStore) {
      const hasSkuAssortFilter = (skuFilter && hasSkuByStore) || (assortmentFilter && hasAssortmentByStore);
      const hasBrandSkuFilter  = brandFilter && hasSkuByStore && Object.keys(skuSubcategory).length > 0;
      if ((hasSkuAssortFilter || hasBrandSkuFilter) && Object.keys(skuSubcategory).length > 0) {
        // Build a per-store subcategory lookup restricted to the selected SKUs/assortments/brands
        const selectedAssortments = assortmentFilter ? new Set([].concat(assortmentFilter)) : null;
        const selectedSkus = skuFilter ? new Set([].concat(skuFilter)) : null;
        const selectedBrands = brandFilter
          ? new Set([].concat(brandFilter).map(b => b.toUpperCase()))
          : null;
        const subcatFromSkus = {};
        for (const [storeKey, storeSKUs] of Object.entries(skuByStore)) {
          for (const [skuName, vals] of Object.entries(storeSKUs)) {
            if (selectedSkus && !selectedSkus.has(skuName)) continue;
            if (selectedAssortments && !selectedAssortments.has(deriveAssortment(skuName))) continue;
            if (selectedBrands) {
              const upper = skuName.toUpperCase();
              if (![...selectedBrands].some(b => upper.startsWith(b + ' ') || upper === b)) continue;
            }
            const subcat = skuSubcategory[skuName];
            if (!subcat) continue;
            if (!subcatFromSkus[storeKey]) subcatFromSkus[storeKey] = {};
            const e = subcatFromSkus[storeKey][subcat] || (subcatFromSkus[storeKey][subcat] = {
              ww_ty: 0, ww_py: null, coles_ty: 0, coles_py: null, ww_units_ty: 0, coles_units_ty: 0,
            });
            e.ww_ty += vals.ww_ty || 0;  e.ww_units_ty += vals.ww_units_ty || 0;
            if (vals.ww_py != null) e.ww_py = (e.ww_py || 0) + vals.ww_py;
            e.coles_ty += vals.coles_ty || 0;  e.coles_units_ty += vals.coles_units_ty || 0;
            if (vals.coles_py != null) e.coles_py = (e.coles_py || 0) + vals.coles_py;
          }
        }
        return toDisplay(deriveProductRows(subcatFromSkus, null, null), rf, measure);
      }
      return toDisplay(deriveProductRows(subcatByStore, hasBrandByStore ? brandByStore : null, brandFilter), rf, measure);
    }
    return toDisplay(sumWeeksWithFallback('subcategory'), rf, measure);
  })();

  // Retailer card color per row
  function retailerRowColor(name) {
    if (name.includes('Woolworths')) return WW_COLOR;
    if (name.includes('Coles'))      return COLES_COLOR;
    return BOTH_COLOR;
  }

  const cardColor  = rf === 'coles' ? COLES_COLOR : rf === 'ww' ? WW_COLOR : BOTH_COLOR;
  const weeks      = storeData?.weeks || [];
  const hasFilters = retailerFilter || stateFilter || clusterFilter || sizeFilter || brandFilter
    || assortmentFilter || skuFilter || regionFilter || tourismFilter || gondolaFrontFilter || gondolaBackFilter
    || ethnicFilter || resortFilter || nutBayFilter || mpackBayFilter
    || groupzoneFilter || subcategoryFilter || storeFilter;

  const retailerLabel = retailerFilter === 'ww' ? 'Woolworths' : retailerFilter === 'coles' ? 'Coles' : null;

  // Region/Tourism: also update stateFilter (deduplicated) for dimension cross-filtering
  function handleRegionClick(name, e) {
    toggleFilter(setRegionFilter)(name, e);
    setTourismFilter(null);
    const st = REGION_STATE[name];
    if (st) {
      setStateFilter(prev => {
        const multi = e?.ctrlKey || e?.metaKey;
        const arr = Array.isArray(prev) ? prev : (prev ? [prev] : []);
        if (multi) {
          const next = arr.includes(st) ? arr.filter(x => x !== st) : [...arr, st];
          return next.length === 0 ? null : [...new Set(next)];
        }
        return arr.length === 1 && arr[0] === st ? null : [st];
      });
    }
  }
  function handleTourismClick(name, e) {
    toggleFilter(setTourismFilter)(name, e);
    setRegionFilter(null);
    const st = TOURISM_STATE[name];
    if (st) {
      setStateFilter(prev => {
        const multi = e?.ctrlKey || e?.metaKey;
        const arr = Array.isArray(prev) ? prev : (prev ? [prev] : []);
        if (multi) {
          const next = arr.includes(st) ? arr.filter(x => x !== st) : [...arr, st];
          return next.length === 0 ? null : [...new Set(next)];
        }
        return arr.length === 1 && arr[0] === st ? null : [st];
      });
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* ── Filter bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: 'var(--bg-elevated)', borderRadius: 8, padding: '8px 14px' }}>

        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Week</span>
        <WeekPicker weeks={weeks} selected={selectedWeeks} onChange={setSelectedWeeks} latestWeek={storeData?.latest_week} />

        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 2px' }} />

        {/* RSV / Units toggle */}
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Measure</span>
        {['RSV', 'Units'].map(m => (
          <button key={m} className={measure === m ? 'pill-active' : 'pill-inactive'} onClick={() => setMeasure(m)}>{m}</button>
        ))}
        {measure === 'Units' && !hasWeeklyPanels && (
          <span style={{ fontSize: 11, color: 'var(--warn)' }}>· Units data available after rebuild</span>
        )}

        {/* Active filters */}
        {retailerLabel     && <Badge label="Retailer"   value={retailerLabel}     onClear={() => setRetailerFilter(null)} />}
        {stateFilter       && <Badge label="State"      value={[].concat(stateFilter).join(', ')}       onClear={() => { setStateFilter(null); setRegionFilter(null); setTourismFilter(null); }} />}
        {clusterFilter     && <Badge label="Cluster"    value={[].concat(clusterFilter).join(', ')}     onClear={() => setClusterFilter(null)} />}
        {sizeFilter        && <Badge label="Size"       value={[].concat(sizeFilter).join(', ')}        onClear={() => setSizeFilter(null)} />}
        {brandFilter       && <Badge label="Brand"       value={[].concat(brandFilter).join(', ')}       onClear={() => setBrandFilter(null)} />}
        {assortmentFilter  && <Badge label="Assortment" value={[].concat(assortmentFilter).join(', ')}  onClear={() => setAssortmentFilter(null)} />}
        {skuFilter         && <Badge label="SKU"        value={[].concat(skuFilter).join(', ')}         onClear={() => setSkuFilter(null)} />}
        {regionFilter      && <Badge label="Region"     value={[].concat(regionFilter).join(', ')}      onClear={() => setRegionFilter(null)} />}
        {tourismFilter     && <Badge label="Tourism"    value={[].concat(tourismFilter).join(', ')}     onClear={() => setTourismFilter(null)} />}
        {gondolaFrontFilter && <Badge label="Gondola F" value={[].concat(gondolaFrontFilter).join(', ')} onClear={() => setGondolaFrontFilter(null)} />}
        {gondolaBackFilter  && <Badge label="Gondola B" value={[].concat(gondolaBackFilter).join(', ')}  onClear={() => setGondolaBackFilter(null)} />}
        {ethnicFilter      && <Badge label="Ethnic"     value={[].concat(ethnicFilter).join(', ')}      onClear={() => setEthnicFilter(null)} />}
        {resortFilter      && <Badge label="Resort"     value={[].concat(resortFilter).join(', ')}      onClear={() => setResortFilter(null)} />}
        {nutBayFilter      && <Badge label="Nut Bay"    value={[].concat(nutBayFilter).join(', ')}      onClear={() => setNutBayFilter(null)} />}
        {mpackBayFilter    && <Badge label="Mpack Bay"  value={[].concat(mpackBayFilter).join(', ')}    onClear={() => setMpackBayFilter(null)} />}
        {groupzoneFilter   && <Badge label="Group Zone" value={[].concat(groupzoneFilter).join(', ')}   onClear={() => setGroupzoneFilter(null)} />}
        {subcategoryFilter && <Badge label="Subcat"     value={[].concat(subcategoryFilter).join(', ')} onClear={() => setSubcategoryFilter(null)} />}
        {storeFilter       && <Badge label="Store"      value={`${[].concat(storeFilter).length} selected`} onClear={() => setStoreFilter(null)} />}
        {hasFilters        && <span style={{ fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer' }} onClick={clearAll}>Clear all</span>}

        {storeData && (
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
            Built <span style={{ color: 'var(--text-primary)' }}>{storeData.built_at}</span>
            {selectedWeeks.length > 1 && <span style={{ color: 'var(--warn)', marginLeft: 8 }}>· {selectedWeeks.length} weeks summed</span>}
          </div>
        )}
      </div>

      {/* ── Grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.4fr', gap: 10, alignItems: 'start' }}>

        {/* Col 1 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Retailer — click row to set retailer filter */}
          <div className="card" style={{ padding: '10px 14px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
              Retailer Measure vs. PY
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6, fontSize: 9 }}>click to filter</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 5, marginBottom: 2 }}>
              <div style={{ width: 64, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>AC ↓</div>
              <div style={{ width: 60, fontSize: 10, color: 'var(--text-muted)', textAlign: 'right' }}>ΔPY</div>
            </div>
            {(() => {
              const maxVal = Math.max(...retailerDisplayRows.map(r => r.ty), 1);
              return retailerDisplayRows.map(r => (
                <HRow key={r.name} name={r.name} value={r.ty}
                  delta={r.py != null ? r.ty - r.py : null}
                  maxValue={maxVal}
                  color={retailerRowColor(r.name)}
                  nameWidth={154} measure={measure}
                  dimmed={retailerFilter != null && !r.name.toLowerCase().includes(retailerFilter === 'ww' ? 'woolworth' : 'coles')}
                  onClick={() => {
                    const key = r.name.includes('Woolworths') ? 'ww' : 'coles';
                    setRetailerFilter(prev => prev === key ? null : key);
                  }}
                />
              ));
            })()}
            {retailerDisplayRows.length > 0 && (
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 154, fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0, fontWeight: 600 }}>Total</div>
                <div style={{ flex: 1 }} />
                <div style={{ width: 64, fontSize: 11, color: 'var(--text-primary)', textAlign: 'right', fontWeight: 600 }}>
                  {measure === 'RSV' ? fmtRSV(retailerDisplayRows.reduce((s, r) => s + r.ty, 0)) : fmtUnits(retailerDisplayRows.reduce((s, r) => s + r.ty, 0))}
                </div>
                <div style={{ width: 60 }} />
              </div>
            )}
          </div>

          <HCard title="State Measure vs. PY" rows={stateRows} color={cardColor} nameWidth={42} measure={measure}
            note={derivedNote} activeFilter={stateFilter} onRowClick={toggleFilter(setStateFilter)} />
          <HCard title="Region Measure vs. PY" rows={regionRows} color={cardColor} nameWidth={160} measure={measure}
            note={derivedNote || (stateFilter ? `${periodNote()} · ${[].concat(stateFilter).join(', ')}` : periodNote())}
            activeFilter={regionFilter} onRowClick={handleRegionClick} />
          <HCard title="Tourist Measure vs. PY" rows={tourismRows} color={cardColor} nameWidth={160} measure={measure}
            note={derivedNote || (stateFilter ? `${periodNote()} · ${[].concat(stateFilter).join(', ')}` : periodNote())}
            activeFilter={tourismFilter} onRowClick={handleTourismClick} />
        </div>

        {/* Col 2 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <ExpandableBrandCard title="Brand Measure vs. PY" brandRows={brandRows}
            assortmentsByBrand={assortmentsByBrand}
            activeFilter={brandFilter} onBrandClick={toggleFilter(setBrandFilter)}
            assortmentFilter={assortmentFilter} onAssortmentClick={toggleFilter(setAssortmentFilter)}
            skuFilter={skuFilter} onSkuClick={toggleFilter(setSkuFilter)}
            measure={measure}
            retailerFilter={rf}
            note={(canDerive && (hasBrandByStore || hasAssortmentByStore || hasSkuByStore)) ? derivedNote : hasWeeklyBrand ? periodNote('STORE') : 'STORE YTD'} />
          <ExpandableClusterCard rawRows={clusterRaw} rf={rf} measure={measure}
            activeFilter={clusterFilter} onItemClick={toggleFilter(setClusterFilter)}
            note={derivedNote || (stateFilter ? [].concat(stateFilter).join(', ') + ' only' : undefined)} />
          <HCard title="Store Size Measure vs. PY" rows={sizeRows} color={cardColor} nameWidth={42} measure={measure}
            activeFilter={sizeFilter} onRowClick={toggleFilter(setSizeFilter)}
            note={derivedNote || (stateFilter ? [].concat(stateFilter).join(', ') + ' only' : undefined)} />
          <HCard title="Subcategory Measure vs. PY" rows={subcategoryRows} color={cardColor} nameWidth={140} measure={measure}
            note={derivedNote || periodNote('STORE')}
            activeFilter={subcategoryFilter} onRowClick={toggleFilter(setSubcategoryFilter)} />
          <ExpandableGroupZoneCard rawRows={groupzoneRaw} rf={rf} measure={measure}
            activeFilter={groupzoneFilter} onGroupZoneClick={toggleFilter(setGroupzoneFilter)}
            note={derivedNote || periodNote()} />
        </div>

        {/* Col 3 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <HCard title="WW Front Gondola End vs. PY" rows={gondolaFront} color={WW_COLOR} nameWidth={42} measure={measure} note={derivedNote || periodNote('WW')} noDataMsg="Hidden — Coles filter active"
            activeFilter={gondolaFrontFilter} onRowClick={toggleFilter(setGondolaFrontFilter)} />
          <HCard title="WW Back Gondola End vs. PY"  rows={gondolaBack}  color={WW_COLOR} nameWidth={42} measure={measure} note={derivedNote || periodNote('WW')} noDataMsg="Hidden — Coles filter active"
            activeFilter={gondolaBackFilter}  onRowClick={toggleFilter(setGondolaBackFilter)} />
          <HCard title="WW Ethnic Tiers vs. PY"      rows={ethnicTiers}  color={WW_COLOR} nameWidth={70} measure={measure} note={derivedNote || periodNote('WW')} noDataMsg="Hidden — Coles filter active"
            activeFilter={ethnicFilter}       onRowClick={toggleFilter(setEthnicFilter)} />
          <HCard title="WW Resort Stores vs. PY"     rows={resortRows}   color={WW_COLOR} nameWidth={90} measure={measure} note={derivedNote || periodNote('WW')} noDataMsg="Hidden — Coles filter active"
            activeFilter={resortFilter}       onRowClick={toggleFilter(setResortFilter)} />
          <HCard title="Nut Bay Size"        rows={nutBayRows}   color={cardColor} nameWidth={42} measure={measure} note={derivedNote || periodNote()}
            activeFilter={nutBayFilter}   onRowClick={toggleFilter(setNutBayFilter)} />
          <HCard title="Multipacks Bay Size" rows={mpackBayRows} color={cardColor} nameWidth={42} measure={measure} note={derivedNote || periodNote()}
            activeFilter={mpackBayFilter} onRowClick={toggleFilter(setMpackBayFilter)} />
        </div>

        {/* Col 4 */}
        <div>
          <StorePanel
            allStores={allStores}
            storeAttrs={storeAttrs}
            retailerFilter={retailerFilter}
            stateFilter={stateFilter} clusterFilter={clusterFilter} sizeFilter={sizeFilter}
            measure={measure} weeksData={weeksData} isDateFiltered={isDateFiltered} storeWeeklyRsv={storeWeeklyRsv}
            regionFilter={regionFilter} tourismFilter={tourismFilter}
            gondolaFrontFilter={gondolaFrontFilter} gondolaBackFilter={gondolaBackFilter}
            ethnicFilter={ethnicFilter} resortFilter={resortFilter}
            nutBayFilter={nutBayFilter} mpackBayFilter={mpackBayFilter}
            groupzoneFilter={groupzoneFilter}
            brandFilter={brandFilter} subcategoryFilter={subcategoryFilter}
            brandByStore={brandByStore} subcatByStore={subcatByStore}
            assortmentFilter={assortmentFilter} assortmentByStore={assortmentByStore}
            skuFilter={skuFilter} skuByStore={skuByStore}
            storeFilter={storeFilter}
            onStoreClick={toggleFilter(setStoreFilter)}
          />
        </div>
      </div>
    </div>
  );
}
